import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { 
  ChevronRight, 
  ChevronLeft, 
  Plus, 
  Trash2, 
  FileText, 
  Users, 
  Download,
  Files,
  FileStack,
  User,
  Save,
  Upload,
  Eye,
  Printer,
  Eraser,
  Check,
  Loader2,
  Mail,
  Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bce96879-a686-4282-a2bd-aa36efb2da0a/artifacts/pm70l8ww_logo%20no%20bkg%20at%20all.png";

// Admin Panel Component
const AdminPanel = ({ onClose, isSuperAdmin }) => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [passwordResult, setPasswordResult] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null); // userId or null
  const [customPassword, setCustomPassword] = useState("");
  const [useCustomPassword, setUseCustomPassword] = useState(false);
  
  // Add User Modal
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", email: "", password: "", license_key: "", is_permanent: false });
  
  // Edit User Modal
  const [showEditModal, setShowEditModal] = useState(null); // user object or null
  const [editData, setEditData] = useState({ username: "", email: "", license_key: "" });

  const fetchUsers = async () => {
    const token = localStorage.getItem('cpr_auth_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
      toast.error("Failed to fetch users");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch users when panel opens and retry if needed
  useEffect(() => {
    fetchUsers();
    
    // Retry after a short delay in case of race condition
    const retryTimeout = setTimeout(() => {
      if (users.length === 0 && !isLoading) {
        fetchUsers();
      }
    }, 500);
    
    return () => clearTimeout(retryTimeout);
  }, []);

  const handleAddUser = async () => {
    if (!newUser.username.trim() || newUser.username.trim().length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (!newUser.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!newUser.password || newUser.password.length < 6) {
      toast.error("Password must be 6 or more characters");
      return;
    }
    // License key is optional if user is permanent or has grace period
    if (!newUser.license_key.trim() && !newUser.is_permanent) {
      // User will have 3-day grace period
    }

    setActionLoading('add');
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/admin/users`, {
        username: newUser.username.trim(),
        email: newUser.email.trim(),
        password: newUser.password,
        license_key: newUser.license_key.trim().toUpperCase() || null,
        is_permanent: newUser.is_permanent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowAddUserModal(false);
        setNewUser({ username: "", email: "", password: "", license_key: "", is_permanent: false });
        fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (user) => {
    setEditData({
      username: user.username || "",
      email: user.email || "",
      license_key: user.license_key || ""
    });
    setShowEditModal(user);
  };

  const handleEditUser = async () => {
    if (!showEditModal) return;
    
    setActionLoading(showEditModal._id);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      
      // Build payload with all editable fields
      const payload = {
        username: editData.username.trim().toLowerCase(),
        email: editData.email.trim().toLowerCase(),
        license_key: editData.license_key.trim().toUpperCase()
      };
      
      console.log("Sending edit payload:", payload);
      
      const response = await axios.put(`${API}/admin/users/${showEditModal._id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("Edit response:", response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowEditModal(null);
        fetchUsers();
      } else {
        toast.error(response.data.message || "Failed to update user");
      }
    } catch (err) {
      console.error("Edit error:", err);
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Failed to update user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    // Prevent double-click issues
    if (actionLoading) return;
    
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setActionLoading(null);
    }
  };

  const openPasswordModal = (userId) => {
    setShowPasswordModal(userId);
    setCustomPassword("");
    setUseCustomPassword(false);
    setPasswordResult(null);
  };

  const handleResetPassword = async () => {
    const userId = showPasswordModal;
    if (!userId) return;
    
    if (useCustomPassword && customPassword.length < 6) {
      toast.error("Password must be 6 or more characters");
      return;
    }
    
    setActionLoading(userId);
    setPasswordResult(null);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const payload = useCustomPassword ? { custom_password: customPassword } : {};
      const response = await axios.post(`${API}/admin/users/${userId}/reset-password`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPasswordResult({
          username: response.data.username,
          password: response.data.new_password
        });
        toast.success("Password reset successfully");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendResetEmail = async (userId, email) => {
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/admin/users/${userId}/send-reset-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset email");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (userId, username, currentStatus) => {
    // Prevent double-click issues
    if (actionLoading) return;
    
    const action = currentStatus ? "remove admin access from" : "grant admin access to";
    if (!window.confirm(`Are you sure you want to ${action} "${username}"?`)) {
      return;
    }
    
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update admin status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePermanent = async (userId, username, currentStatus) => {
    // Prevent double-click issues
    if (actionLoading) return;
    
    const action = currentStatus ? "remove permanent status from" : "make permanent";
    if (!window.confirm(`Are you sure you want to ${action} "${username}"? ${!currentStatus ? "Permanent users don't need a license key." : ""}`)) {
      return;
    }
    
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-permanent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update permanent status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleSuperAdmin = async (userId, username, currentStatus) => {
    // Prevent double-click issues
    if (actionLoading) return;
    
    const action = currentStatus ? "demote from super admin" : "promote to super admin";
    if (!window.confirm(`Are you sure you want to ${action} "${username}"? ${!currentStatus ? "This will give them full admin privileges including user management." : ""}`)) {
      return;
    }
    
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-super-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update super admin status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleBlcpr = async (userId, username, currentStatus) => {
    // Prevent double-click issues
    if (actionLoading) return;
    
    const action = currentStatus ? "revoke BLCPR access from" : "grant BLCPR access to";
    if (!window.confirm(`Are you sure you want to ${action} "${username}"? ${!currentStatus ? "This will allow them to use the Opioid Awareness (BLCPR) course." : ""}`)) {
      return;
    }
    
    setActionLoading(userId);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-blcpr`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update BLCPR access");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.license_key?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-dark-gradient p-8 pt-16">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
            <p className="text-zinc-400 mt-1">Manage users and passwords</p>
          </div>
          <div className="flex gap-3">
            {isSuperAdmin && (
              <Button 
                onClick={() => setShowAddUserModal(true)}
                className="btn-primary-glow"
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={onClose}
              className="border-zinc-700 hover:bg-zinc-800"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to App
            </Button>
          </div>
        </div>

        {/* Add User Modal */}
        {showAddUserModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="card-dark w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="text-xl text-white">Add New User</CardTitle>
                <p className="text-zinc-400 text-sm">Create a user account with a license key</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Username <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    placeholder="Enter username"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    className="input-dark mt-1"
                    data-testid="add-user-username"
                  />
                </div>
                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    placeholder="Enter email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="input-dark mt-1"
                    data-testid="add-user-email"
                  />
                </div>
                <div>
                  <Label>Password <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    placeholder="Enter password (6 or more characters)"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="input-dark mt-1"
                    data-testid="add-user-password"
                  />
                </div>
                <div>
                  <Label>License Key <span className="text-zinc-500">(optional)</span></Label>
                  <Input
                    type="text"
                    placeholder="Enter license key or leave empty"
                    value={newUser.license_key}
                    onChange={(e) => setNewUser({...newUser, license_key: e.target.value.toUpperCase()})}
                    className="input-dark mt-1 font-mono"
                    data-testid="add-user-license"
                    disabled={newUser.is_permanent}
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    {newUser.is_permanent 
                      ? "Permanent users don't need a key" 
                      : "Leave empty for 3-day grace period"}
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
                  <input
                    type="checkbox"
                    id="is_permanent"
                    checked={newUser.is_permanent}
                    onChange={(e) => setNewUser({...newUser, is_permanent: e.target.checked, license_key: e.target.checked ? "" : newUser.license_key})}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-cyan-500"
                  />
                  <label htmlFor="is_permanent" className="text-sm text-zinc-300">
                    Permanent user (no license key required)
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddUserModal(false);
                      setNewUser({ username: "", email: "", password: "", license_key: "", is_permanent: false });
                    }}
                    className="flex-1 border-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddUser}
                    disabled={actionLoading === 'add'}
                    className="flex-1 btn-primary-glow"
                    data-testid="add-user-submit"
                  >
                    {actionLoading === 'add' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Create User"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="card-dark w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="text-xl text-white">Edit User</CardTitle>
                <p className="text-zinc-400 text-sm">
                  {isSuperAdmin ? "Edit username, email, and license key" : "Edit email and license key"}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Username {!isSuperAdmin && <span className="text-zinc-500 text-xs">(read-only)</span>}</Label>
                  <Input
                    type="text"
                    placeholder="Enter username"
                    value={editData.username}
                    onChange={(e) => setEditData({...editData, username: e.target.value.toLowerCase()})}
                    className={`input-dark mt-1 ${!isSuperAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                    disabled={!isSuperAdmin}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="Enter email"
                    value={editData.email}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label>License Key</Label>
                  <Input
                    type="text"
                    placeholder="Enter license key (or leave empty)"
                    value={editData.license_key}
                    onChange={(e) => setEditData({...editData, license_key: e.target.value.toUpperCase()})}
                    className="input-dark mt-1 font-mono"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(null)}
                    className="flex-1 border-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEditUser}
                    disabled={actionLoading === showEditModal._id}
                    className="flex-1 btn-primary-glow"
                  >
                    {actionLoading === showEditModal._id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <Card className="card-dark w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="text-xl text-white">Reset Password</CardTitle>
                <p className="text-zinc-400 text-sm">Choose how to reset the user's password</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Option Toggle */}
                <div className="flex border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setUseCustomPassword(false)}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      !useCustomPassword 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    Generate Password
                  </button>
                  <button
                    onClick={() => setUseCustomPassword(true)}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      useCustomPassword 
                        ? 'bg-cyan-500/20 text-cyan-400' 
                        : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    Custom Password
                  </button>
                </div>

                {/* Custom Password Input */}
                {useCustomPassword && (
                  <div>
                    <Label>New Password</Label>
                    <Input
                      type="text"
                      placeholder="Enter new password (6 or more characters)"
                      value={customPassword}
                      onChange={(e) => setCustomPassword(e.target.value)}
                      className="input-dark mt-1"
                      data-testid="custom-password-input"
                    />
                  </div>
                )}

                {/* Password Result */}
                {passwordResult && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <p className="text-green-400 font-medium mb-2">Password Reset Successful</p>
                    <p className="text-zinc-300 text-sm">
                      User: <span className="font-mono">{passwordResult.username}</span>
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-zinc-300 text-sm">Password:</span>
                      <code className="bg-zinc-800 px-2 py-1 rounded text-sm">{passwordResult.password}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(passwordResult.password);
                          toast.success("Copied!");
                        }}
                        className="text-cyan-400 hover:text-cyan-300 text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPasswordModal(null);
                      setPasswordResult(null);
                    }}
                    className="flex-1 border-zinc-700"
                  >
                    {passwordResult ? "Done" : "Cancel"}
                  </Button>
                  {!passwordResult && (
                    <Button
                      onClick={handleResetPassword}
                      disabled={actionLoading === showPasswordModal || (useCustomPassword && customPassword.length < 6)}
                      className="flex-1 btn-primary-glow"
                      data-testid="confirm-reset-btn"
                    >
                      {actionLoading === showPasswordModal ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Reset Password"
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search */}
        <Card className="card-dark mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by username, email, or license key..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-dark"
                  data-testid="admin-search"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={fetchUsers}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="card-dark">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center p-12 text-zinc-500">
                {searchQuery ? "No users match your search" : "No users found"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900/50 border-b border-zinc-800">
                    <tr>
                      <th className="text-left p-4 text-zinc-400 font-medium">Username</th>
                      <th className="text-left p-4 text-zinc-400 font-medium">Email</th>
                      <th className="text-left p-4 text-zinc-400 font-medium">License Key</th>
                      <th className="text-left p-4 text-zinc-400 font-medium">Status</th>
                      <th className="text-left p-4 text-zinc-400 font-medium">Role</th>
                      <th className="text-right p-4 text-zinc-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user._id} className="border-b border-zinc-800 hover:bg-zinc-900/30">
                        <td className="p-4">
                          <span className="text-white font-medium">{user.username}</span>
                        </td>
                        <td className="p-4 text-zinc-300">{user.email || '-'}</td>
                        <td className="p-4">
                          <span className="font-mono text-sm text-zinc-400">{user.license_key || '-'}</span>
                        </td>
                        <td className="p-4">
                          {user.is_permanent ? (
                            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">Permanent</span>
                          ) : user.license_key ? (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">Licensed</span>
                          ) : user.grace_days_remaining > 0 ? (
                            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                              {user.grace_days_remaining} days left
                            </span>
                          ) : user.grace_days_remaining === 0 ? (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">Suspended</span>
                          ) : (
                            <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          {user.is_super_admin ? (
                            <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded">
                              Super Admin
                            </span>
                          ) : user.is_admin ? (
                            <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">
                              Admin
                            </span>
                          ) : (
                            <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">User</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(user)}
                              disabled={actionLoading === user._id}
                              className="border-zinc-700 hover:bg-zinc-800 text-xs"
                              data-testid={`edit-user-${user.username}`}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPasswordModal(user._id)}
                              disabled={actionLoading === user._id}
                              className="border-zinc-700 hover:bg-zinc-800 text-xs"
                              data-testid={`reset-password-${user.username}`}
                            >
                              {actionLoading === user._id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                "Reset Password"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendResetEmail(user._id, user.email)}
                              disabled={actionLoading === user._id || !user.email}
                              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs"
                              title={!user.email ? "No email address" : "Send reset email"}
                              data-testid={`send-reset-${user.username}`}
                            >
                              {actionLoading === user._id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <>
                                  <Mail className="w-3 h-3 mr-1" />
                                  Send Reset
                                </>
                              )}
                            </Button>
                            {!['andy', 'biggestlittlecpr'].includes(user.username?.toLowerCase()) && (
                              <>
                                {isSuperAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleTogglePermanent(user._id, user.username, user.is_permanent); }}
                                    disabled={actionLoading === user._id || actionLoading !== null}
                                    className={`text-xs ${user.is_permanent 
                                      ? 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10' 
                                      : 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10'
                                    }`}
                                    data-testid={`toggle-permanent-${user.username}`}
                                  >
                                    {actionLoading === user._id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : user.is_permanent ? (
                                      "Remove Permanent"
                                    ) : (
                                      "Make Permanent"
                                    )}
                                  </Button>
                                )}
                                {/* BLCPR Access Toggle */}
                                {isSuperAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleToggleBlcpr(user._id, user.username, user.blcpr_access); }}
                                    disabled={actionLoading === user._id || actionLoading !== null}
                                    className={`text-xs ${user.blcpr_access 
                                      ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10' 
                                      : 'border-zinc-600/30 text-zinc-400 hover:bg-zinc-500/10'
                                    }`}
                                    data-testid={`toggle-blcpr-${user.username}`}
                                  >
                                    {actionLoading === user._id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : user.blcpr_access ? (
                                      "BLCPR ✓"
                                    ) : (
                                      "BLCPR"
                                    )}
                                  </Button>
                                )}
                                {isSuperAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleToggleAdmin(user._id, user.username, user.is_admin); }}
                                    disabled={actionLoading === user._id || actionLoading !== null}
                                    className={`text-xs ${user.is_admin 
                                      ? 'border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10' 
                                      : 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                                    }`}
                                    data-testid={`toggle-admin-${user.username}`}
                                  >
                                    {actionLoading === user._id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : user.is_admin ? (
                                      "Remove Admin"
                                    ) : (
                                      "Make Admin"
                                    )}
                                  </Button>
                                )}
                                {/* Promote to Super Admin - only for existing admins */}
                                {isSuperAdmin && user.is_admin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleToggleSuperAdmin(user._id, user.username, user.is_super_admin); }}
                                    disabled={actionLoading === user._id || actionLoading !== null}
                                    className={`text-xs ${user.is_super_admin 
                                      ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10' 
                                      : 'border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10'
                                    }`}
                                    data-testid={`toggle-super-admin-${user.username}`}
                                  >
                                    {actionLoading === user._id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : user.is_super_admin ? (
                                      "Demote Super"
                                    ) : (
                                      "Make Super"
                                    )}
                                  </Button>
                                )}
                                {isSuperAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteUser(user._id, user.username); }}
                                    disabled={actionLoading === user._id || actionLoading !== null}
                                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                                    data-testid={`delete-user-${user.username}`}
                                  >
                                    {actionLoading === user._id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Trash2 className="w-3 h-3" />
                                    )}
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-4 text-center text-zinc-500 text-sm">
          Showing {filteredUsers.length} of {users.length} users
        </div>
      </div>
    </div>
  );
};

// License Gate Component - Handles authentication and license validation
const LicenseGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // View modes: 'login', 'register', 'forgot', 'verify_code', 'reset'
  const [viewMode, setViewMode] = useState('login');
  
  // License key modal for users in grace period
  const [showLicenseKeyModal, setShowLicenseKeyModal] = useState(false);
  const [newLicenseKey, setNewLicenseKey] = useState("");
  const [licenseKeyError, setLicenseKeyError] = useState("");
  const [licenseKeySubmitting, setLicenseKeySubmitting] = useState(false);
  
  // Form states
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check for stored token on mount
  useEffect(() => {
    const checkStoredAuth = async () => {
      const storedToken = localStorage.getItem('cpr_auth_token');
      
      if (storedToken) {
        try {
          // Verify token is still valid and license is active
          const response = await axios.post(
            `${API}/auth/verify-token`,
            {},
            { headers: { Authorization: `Bearer ${storedToken}` } }
          );
          
          if (response.data.success) {
            localStorage.setItem('cpr_blcpr_access', response.data.blcpr_access ? 'true' : 'false');
            localStorage.setItem('cpr_is_super_admin', response.data.is_super_admin ? 'true' : 'false');
            setCurrentUser({
              username: response.data.username,
              licenseKey: response.data.license_key,
              isAdmin: response.data.is_admin,
              isSuperAdmin: response.data.is_super_admin,
              needsLicenseKey: response.data.needs_license_key,
              graceDaysRemaining: response.data.grace_days_remaining,
              isPermanent: response.data.is_permanent,
              blcprAccess: response.data.blcpr_access
            });
            setIsAuthenticated(true);
          } else {
            // Token invalid or license expired
            localStorage.removeItem('cpr_auth_token');
            // Check if logged in elsewhere
            if (response.data.message === "SESSION_EXPIRED_ELSEWHERE") {
              setError("You have been logged out because your account was signed in on another device.");
            } else {
              setError(response.data.message);
            }
          }
        } catch (err) {
          // Token verification failed
          localStorage.removeItem('cpr_auth_token');
        }
      }
      setIsChecking(false);
    };
    
    checkStoredAuth();
  }, []);

  // Periodic session validation (every 30 seconds) to detect if logged in elsewhere
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const validateSession = async () => {
      const storedToken = localStorage.getItem('cpr_auth_token');
      if (!storedToken) return;
      
      try {
        const response = await axios.post(
          `${API}/auth/verify-token`,
          {},
          { headers: { Authorization: `Bearer ${storedToken}` } }
        );
        
        // ONLY log out if specifically logged in elsewhere - ignore other failures
        if (!response.data.success && response.data.message === "SESSION_EXPIRED_ELSEWHERE") {
          localStorage.removeItem('cpr_auth_token');
          localStorage.removeItem('cpr_blcpr_access');
          localStorage.removeItem('cpr_is_super_admin');
          setIsAuthenticated(false);
          setCurrentUser(null);
          toast.error("You have been logged out because your account was signed in on another device.");
        }
      } catch (err) {
        // Silently fail - will retry on next interval
      }
    };
    
    const interval = setInterval(validateSession, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleSubmitLicenseKey = async () => {
    if (!newLicenseKey.trim()) {
      setLicenseKeyError("Please enter a license key");
      return;
    }

    setLicenseKeySubmitting(true);
    setLicenseKeyError("");

    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/auth/submit-license-key`, {
        license_key: newLicenseKey.trim().toUpperCase()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success("License key registered successfully! Your account is now active.");
        setCurrentUser(prev => ({
          ...prev,
          licenseKey: newLicenseKey.trim().toUpperCase(),
          needsLicenseKey: false,
          graceDaysRemaining: null,
          isSuspended: false,
          daysUntilDeletion: null
        }));
        setShowLicenseKeyModal(false);
        setNewLicenseKey("");
      } else {
        setLicenseKeyError(response.data.message);
      }
    } catch (err) {
      setLicenseKeyError(err.response?.data?.message || "Failed to submit license key");
    } finally {
      setLicenseKeySubmitting(false);
    }
  };

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      setError("Please enter username and password");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password
      });

      if (response.data.success) {
        localStorage.setItem('cpr_auth_token', response.data.token);
        localStorage.setItem('cpr_blcpr_access', response.data.blcpr_access ? 'true' : 'false');
        localStorage.setItem('cpr_is_super_admin', response.data.is_super_admin ? 'true' : 'false');
        setCurrentUser({
          username: response.data.username,
          licenseKey: response.data.license_key,
          isAdmin: response.data.is_admin,
          isSuperAdmin: response.data.is_super_admin,
          needsLicenseKey: response.data.needs_license_key,
          graceDaysRemaining: response.data.grace_days_remaining,
          isPermanent: response.data.is_permanent,
          isSuspended: response.data.is_suspended,
          daysUntilDeletion: response.data.days_until_deletion,
          blcprAccess: response.data.blcpr_access
        });
        setIsAuthenticated(true);
        // Dispatch custom event to notify App to load preferences
        window.dispatchEvent(new Event('cpr-auth-login'));
        if (response.data.is_suspended) {
          toast.warning(`Account suspended! Enter a license key within ${response.data.days_until_deletion} days or your account will be deleted.`);
          setShowLicenseKeyModal(true);
        } else if (response.data.needs_license_key) {
          toast.info(`You have ${response.data.grace_days_remaining} days to enter a license key`);
        } else {
          toast.success("Welcome back!");
        }
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async () => {
    if (!username.trim()) {
      setError("Please enter a username");
      return;
    }
    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    if (password.length < 6) {
      setError("Password must be 6 or more characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!licenseKey.trim()) {
      setError("Please enter your license key");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/register`, {
        username: username.trim(),
        email: email.trim().toLowerCase(),
        password: password,
        license_key: licenseKey.trim()
      });

      if (response.data.success) {
        localStorage.setItem('cpr_auth_token', response.data.token);
        setCurrentUser({
          username: response.data.username,
          licenseKey: response.data.license_key,
          isAdmin: response.data.is_admin || false,
          isSuperAdmin: response.data.is_super_admin || false
        });
        setIsAuthenticated(true);
        toast.success("Account created successfully!");
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cpr_auth_token');
    localStorage.removeItem('cpr_blcpr_access');
    localStorage.removeItem('cpr_is_super_admin');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setUsername("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setLicenseKey("");
    setViewMode('login');
    toast.success("Logged out successfully");
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase()
      });
      
      if (response.data.success) {
        setSuccessMessage("A 6-digit code has been sent to your email.");
        setViewMode('verify_code');
      } else {
        setError(response.data.message || "Failed to send reset code.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send reset code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!resetCode.trim() || resetCode.trim().length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/auth/verify-reset-code`, {
        email: email.trim().toLowerCase(),
        code: resetCode.trim()
      });
      
      if (response.data.success) {
        setResetToken(response.data.temp_token);
        setSuccessMessage("Code verified! Please enter your new password.");
        setViewMode('reset');
        setResetCode("");
      } else {
        setError(response.data.message || "Invalid code. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (password.length < 6) {
      setError("Password must be 6 or more characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: password
      });
      
      if (response.data.success) {
        setSuccessMessage("Password reset successfully! You can now log in.");
        setPassword("");
        setConfirmPassword("");
        setResetToken("");
        setEmail("");
        setTimeout(() => {
          setViewMode('login');
          setSuccessMessage("");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to reset password.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (viewMode === 'login') {
        handleLogin();
      } else if (viewMode === 'register') {
        handleRegister();
      } else if (viewMode === 'forgot') {
        handleForgotPassword();
      } else if (viewMode === 'verify_code') {
        handleVerifyCode();
      } else if (viewMode === 'reset') {
        handleResetPassword();
      }
    }
  };

  const clearForm = () => {
    setUsername("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setLicenseKey("");
    setResetCode("");
    setError("");
    setSuccessMessage("");
  };

  // Show loading while checking stored auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-dark-gradient flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <p className="text-zinc-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show login/register screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-gradient flex items-center justify-center p-4">
        <Card className="card-dark w-full max-w-md">
          <CardHeader className="text-center">
            <img 
              src={LOGO_URL} 
              alt="Biggest Little CPR" 
              className="h-20 mx-auto mb-4"
            />
            <CardTitle className="text-2xl">
              <span className="text-cyan-400">CPR</span> Paperwork Assistant
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              {viewMode === 'login' && 'Sign in to continue'}
              {viewMode === 'register' && 'Create your account'}
              {viewMode === 'forgot' && 'Reset your password'}
              {viewMode === 'verify_code' && 'Enter verification code'}
              {viewMode === 'reset' && 'Set your new password'}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Toggle between Login and Register (hide for forgot/reset) */}
            {(viewMode === 'login' || viewMode === 'register') && (
              <div className="flex border border-zinc-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => { setViewMode('login'); clearForm(); }}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'login' 
                      ? 'bg-cyan-500/20 text-cyan-400 border-r border-zinc-700' 
                      : 'text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  Login
                </button>
                <button
                  onClick={() => { setViewMode('register'); clearForm(); }}
                  className={`flex-1 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'register' 
                      ? 'bg-cyan-500/20 text-cyan-400' 
                      : 'text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  Register
                </button>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
            
            {successMessage && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-400 text-sm">{successMessage}</p>
              </div>
            )}

            {/* Login Form */}
            {viewMode === 'login' && (
              <>
                <div>
                  <Label>Username</Label>
                  <Input
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setError(""); }}
                    onKeyPress={handleKeyPress}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="login-username"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    onKeyPress={handleKeyPress}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="login-password"
                  />
                </div>
                <Button
                  onClick={handleLogin}
                  className="btn-primary-glow w-full"
                  disabled={isSubmitting}
                  data-testid="login-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
                <button
                  onClick={() => { setViewMode('forgot'); clearForm(); }}
                  className="text-cyan-400 hover:text-cyan-300 text-sm w-full text-center mt-2"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </>
            )}

            {/* Register Form */}
            {viewMode === 'register' && (
              <>
                <div>
                  <Label>License Key <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={licenseKey}
                    onChange={(e) => { setLicenseKey(e.target.value.toUpperCase()); setError(""); }}
                    className="input-dark mt-1 font-mono"
                    disabled={isSubmitting}
                    data-testid="register-license"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Enter the license key from your purchase</p>
                </div>
                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="register-email"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Used for password recovery</p>
                </div>
                <div>
                  <Label>Username <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setError(""); }}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="register-username"
                  />
                </div>
                <div>
                  <Label>Password <span className="text-red-500">*</span></Label>
                  <Input
                    type="password"
                    placeholder="Create a password (6 or more characters)"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="register-password"
                  />
                </div>
                <div>
                  <Label>Confirm Password <span className="text-red-500">*</span></Label>
                  <Input
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                    onKeyPress={handleKeyPress}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="register-confirm-password"
                  />
                </div>
                <Button
                  onClick={handleRegister}
                  className="btn-primary-glow w-full"
                  disabled={isSubmitting}
                  data-testid="register-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </>
            )}
            
            {/* Forgot Password Form */}
            {viewMode === 'forgot' && (
              <>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); setSuccessMessage(""); }}
                    onKeyPress={handleKeyPress}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="forgot-email"
                  />
                  <p className="text-xs text-zinc-500 mt-1">We'll send a 6-digit code to this email</p>
                </div>
                <Button
                  onClick={handleForgotPassword}
                  className="btn-primary-glow w-full"
                  disabled={isSubmitting}
                  data-testid="forgot-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Code"
                  )}
                </Button>
                <button
                  onClick={() => { setViewMode('login'); clearForm(); }}
                  className="text-zinc-400 hover:text-zinc-300 text-sm w-full text-center mt-2"
                >
                  Back to Login
                </button>
              </>
            )}
            
            {/* Verify Code Form */}
            {viewMode === 'verify_code' && (
              <>
                <div>
                  <Label>Enter 6-Digit Code</Label>
                  <Input
                    type="text"
                    placeholder="Enter the code from your email"
                    value={resetCode}
                    onChange={(e) => { 
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setResetCode(val); 
                      setError(""); 
                    }}
                    onKeyPress={handleKeyPress}
                    className="input-dark mt-1 text-center text-2xl tracking-widest font-mono"
                    disabled={isSubmitting}
                    data-testid="verify-code-input"
                    maxLength={6}
                  />
                  <p className="text-xs text-zinc-500 mt-1">Code sent to {email}</p>
                </div>
                <Button
                  onClick={handleVerifyCode}
                  className="btn-primary-glow w-full"
                  disabled={isSubmitting || resetCode.length !== 6}
                  data-testid="verify-code-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Verify Code"
                  )}
                </Button>
                <button
                  onClick={() => { setViewMode('forgot'); setResetCode(""); setError(""); setSuccessMessage(""); }}
                  className="text-zinc-400 hover:text-zinc-300 text-sm w-full text-center mt-2"
                >
                  Didn't receive code? Try again
                </button>
              </>
            )}
            
            {/* Password Reset Form */}
            {viewMode === 'reset' && (
              <>
                <div>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter new password (6 or more characters)"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(""); }}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="reset-password"
                  />
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                    onKeyPress={handleKeyPress}
                    className="input-dark mt-1"
                    disabled={isSubmitting}
                    data-testid="reset-confirm-password"
                  />
                </div>
                <Button
                  onClick={handleResetPassword}
                  className="btn-primary-glow w-full"
                  disabled={isSubmitting}
                  data-testid="reset-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Resetting...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
                <button
                  onClick={() => { setViewMode('login'); clearForm(); setResetToken(""); }}
                  className="text-zinc-400 hover:text-zinc-300 text-sm w-full text-center mt-2"
                >
                  Back to Login
                </button>
              </>
            )}
            
            <div className="text-center text-sm text-zinc-500 pt-4 border-t border-zinc-800">
              <p>Don't have a license?</p>
              <a 
                href="https://www.biggestlittlecpr.com/product/paperwork-generator/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Purchase at biggestlittlecpr.com
              </a>
            </div>
          </CardContent>
        </Card>
        
        <div className="fixed bottom-4 text-center w-full">
          <p className="text-zinc-600 text-xs">v3.0 - Biggest Little CPR</p>
        </div>
      </div>
    );
  }

  // User is authenticated, render the app with logout option
  return (
    <>
      {/* SUSPENDED ACCOUNT - Full-screen blocking modal */}
      {currentUser?.isSuspended && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[10001] p-4" data-testid="suspended-modal">
          <Card className="card-dark w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <CardTitle className="text-2xl text-red-400">Account Suspended</CardTitle>
              <p className="text-zinc-400 text-sm mt-2">
                Your grace period has expired. Please enter a valid license key to reactivate your account.
              </p>
              {currentUser?.daysUntilDeletion > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4">
                  <p className="text-red-400 text-sm font-medium">
                    Your account will be permanently deleted in {currentUser?.daysUntilDeletion} day{currentUser?.daysUntilDeletion !== 1 ? 's' : ''} if no license key is provided.
                  </p>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {licenseKeyError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{licenseKeyError}</p>
                </div>
              )}
              <div>
                <Label>License Key</Label>
                <Input
                  type="text"
                  placeholder="Enter your license key"
                  value={newLicenseKey}
                  onChange={(e) => { setNewLicenseKey(e.target.value.toUpperCase()); setLicenseKeyError(""); }}
                  className="input-dark mt-1 font-mono"
                  disabled={licenseKeySubmitting}
                  data-testid="suspended-license-input"
                />
              </div>
              <Button
                onClick={handleSubmitLicenseKey}
                disabled={licenseKeySubmitting || !newLicenseKey.trim()}
                className="w-full btn-primary-glow"
                data-testid="suspended-submit-key-btn"
              >
                {licenseKeySubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Submit Key & Reactivate Account"
                )}
              </Button>
              <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                <a
                  href="https://www.biggestlittlecpr.com/product/paperwork-generator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 text-sm"
                >
                  Purchase a license key
                </a>
                <button
                  onClick={handleLogout}
                  className="text-zinc-500 hover:text-red-400 text-sm transition-colors"
                >
                  Logout
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* License Key Required Banner (Grace Period - not suspended) */}
      {currentUser?.needsLicenseKey && !currentUser?.isSuspended && (
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-yellow-500/90 text-black py-2 px-4 text-center">
          <span className="font-medium">
            You have {currentUser?.graceDaysRemaining} day{currentUser?.graceDaysRemaining !== 1 ? 's' : ''} to enter a license key.
          </span>
          <button
            onClick={() => setShowLicenseKeyModal(true)}
            className="ml-3 bg-black text-white px-3 py-1 rounded text-sm hover:bg-zinc-800"
          >
            Enter Key Now
          </button>
          <a
            href="https://www.biggestlittlecpr.com/product/paperwork-generator/"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 underline text-sm"
          >
            No key yet? Purchase here
          </a>
        </div>
      )}
      
      {/* License Key Modal */}
      {showLicenseKeyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000]">
          <Card className="card-dark w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-xl text-white">Enter License Key</CardTitle>
              <p className="text-zinc-400 text-sm">
                {currentUser?.graceDaysRemaining > 0 
                  ? `You have ${currentUser?.graceDaysRemaining} days remaining to enter your license key.`
                  : 'Please enter your license key to continue.'}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {licenseKeyError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{licenseKeyError}</p>
                </div>
              )}
              <div>
                <Label>License Key</Label>
                <Input
                  type="text"
                  placeholder="Enter your license key"
                  value={newLicenseKey}
                  onChange={(e) => { setNewLicenseKey(e.target.value.toUpperCase()); setLicenseKeyError(""); }}
                  className="input-dark mt-1 font-mono"
                  disabled={licenseKeySubmitting}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowLicenseKeyModal(false); setNewLicenseKey(""); setLicenseKeyError(""); }}
                  className="flex-1 border-zinc-700"
                >
                  Later
                </Button>
                <Button
                  onClick={handleSubmitLicenseKey}
                  disabled={licenseKeySubmitting}
                  className="flex-1 btn-primary-glow"
                >
                  {licenseKeySubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Submit Key"
                  )}
                </Button>
              </div>
              <div className="text-center pt-2 border-t border-zinc-800">
                <a
                  href="https://www.biggestlittlecpr.com/product/paperwork-generator/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 text-sm"
                >
                  No key yet? Click here to purchase a key
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Admin button - below nav bar, left side */}
      {currentUser?.isAdmin && (
        <div className={`fixed ${currentUser?.needsLicenseKey && !currentUser?.isSuspended ? 'top-24' : 'top-16'} left-4 z-[9999]`} style={{ pointerEvents: 'auto' }}>
          <button
            onClick={() => setViewMode('admin')}
            className="flex items-center gap-2 bg-zinc-900/95 backdrop-blur border border-cyan-400/30 rounded-lg px-3 py-1.5 shadow-lg text-sm text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors"
            data-testid="admin-panel-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {currentUser?.isSuperAdmin ? 'Super Admin' : 'Admin'}
          </button>
        </div>
      )}
      
      {/* User info bar - below nav bar, right side */}
      <div className={`fixed ${currentUser?.needsLicenseKey && !currentUser?.isSuspended ? 'top-24' : 'top-16'} right-4 z-[9999]`} style={{ pointerEvents: 'auto' }}>
        <div className="flex items-center gap-2 bg-zinc-900/95 backdrop-blur border border-zinc-800 rounded-lg px-3 py-1.5 shadow-lg">
          <User className="w-4 h-4 text-cyan-400" />
          <span className="text-sm text-zinc-300">{currentUser?.username}</span>
          <button
            onClick={handleLogout}
            className="text-xs text-zinc-500 hover:text-red-400 ml-2 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
      {viewMode === 'admin' && currentUser?.isAdmin ? (
        <AdminPanel onClose={() => setViewMode('login')} isSuperAdmin={currentUser?.isSuperAdmin} />
      ) : (
        children
      )}
    </>
  );
};

const COURSES = [
  "Opioid Awareness from BLCPR",
  "Basic Life Support (BLS)",
  "HS CPR/AED",
  "HS FA CPR/AED",
  "HS First Aid",
  "HS Total",
  "HS Pediatric First Aid CPR/AED",
  "HS K-12",
  "Instructor Candidate"
];

// Course-specific age options - only courses with age dropdown are listed here
const COURSES_WITH_AGE_OPTIONS = {
  "HS CPR/AED": ["Adult Only", "Adult W/Infant", "Adult W/Child", "Adult W/ Infant & Child"],  // Removed Child & Infant Only
  "HS FA CPR/AED": ["Adult Only", "Adult W/Infant", "Adult W/Child", "Adult W/ Infant & Child"],  // Removed Child & Infant Only
  "HS Total": ["Adult Only", "Adult W/Infant", "Adult W/Child", "Adult W/ Infant & Child"],  // No Child & Infant Only
  "HS Pediatric First Aid CPR/AED": ["Adult W/ Infant & Child", "Child & Infant Only"],  // Only 2 options
  "HS K-12": ["Adult Only", "Adult W/Infant", "Adult W/Child", "Adult W/ Infant & Child", "Child & Infant Only"],
};
// Courses WITHOUT age dropdown: BLS, HS First Aid

// Page transition variants
const pageVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

// Navigation Component with clickable steps
const Navigation = ({ currentStep, onStepClick, canNavigate, stepLabels, totalSteps }) => {
  const labels = stepLabels || ["Class Setup", "Students", "Export"];
  const numSteps = totalSteps || 3;
  
  const steps = labels.map((label, idx) => ({
    num: idx + 1,
    label: label
  }));

  const handleStepClick = (stepNum) => {
    // Can always go back, but can only go forward if canNavigate allows
    if (stepNum < currentStep || canNavigate(stepNum)) {
      onStepClick(stepNum);
    }
  };

  return (
    <nav className="nav-bar px-6 py-4" data-testid="navigation">
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img 
            src={LOGO_URL} 
            alt="Biggest Little CPR" 
            className="h-12 w-auto"
            data-testid="nav-logo"
          />
        </div>
        <div className="flex items-center gap-2">
          {steps.map((step, idx) => (
            <div key={step.num} className="flex flex-col items-center">
              <span className="text-[10px] text-zinc-500 mb-1 whitespace-nowrap">{step.label}</span>
              <div className="flex items-center">
                <button
                  onClick={() => handleStepClick(step.num)}
                  className={`step-dot cursor-pointer hover:scale-110 transition-transform ${currentStep === step.num ? 'active' : ''} ${currentStep > step.num ? 'completed' : ''}`}
                  data-testid={`step-indicator-${step.num}`}
                  title={step.label}
                />
                {idx < steps.length - 1 && (
                  <div className="w-8 h-0.5 bg-zinc-800 mx-1" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Step 1: Class Setup
const ClassSetup = ({ data, setData, onNext }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Check if this course has age options
  const courseAgeOptions = COURSES_WITH_AGE_OPTIONS[data.courseType];
  const needsAgeOption = !!courseAgeOptions;
  
  // Check if BLS is selected (needs certification type)
  const isBLS = data.courseType === "Basic Life Support (BLS)";
  
  // Check if Instructor Candidate is selected
  const isInstructorCandidate = data.courseType === "Instructor Candidate";
  
  // Check if Opioid Training is selected (simplified form)
  const isOpioid = data.courseType === "Opioid Awareness from BLCPR";

  // Check BLCPR access from localStorage - Super admins always have access
  const isSuperAdmin = localStorage.getItem('cpr_is_super_admin') === 'true';
  const hasBlcprAccess = localStorage.getItem('cpr_blcpr_access') === 'true' || isSuperAdmin;
  
  // Filter courses based on BLCPR access
  const availableCourses = COURSES.filter(course => {
    if (course === "Opioid Awareness from BLCPR" && !hasBlcprAccess) {
      return false;
    }
    return true;
  });

  const handleCourseChange = (value) => {
    if (value === "Instructor Candidate") {
      // Reset related fields when switching to Instructor Candidate
      // Don't pre-fill TC info - user must select from their saved TCs
      setData({ 
        ...data, 
        courseType: value, 
        ageOption: "", 
        blsCertType: "", 
        instructorCandidateType: "",
        instructorCandidateInfo: {
          ...data.instructorCandidateInfo,
          tcName: "",
          tcId: "",
          trainingSite: "",
          tcAddress: "",
          tcCityStateZip: ""
        }
      });
    } else if (value === "Opioid Awareness from BLCPR") {
      // For Opioid, always include roster (for signature) but disable the toggle
      setData({ ...data, courseType: value, ageOption: "", blsCertType: "", instructorCandidateType: "", includeRoster: true });
    } else {
      setData({ ...data, courseType: value, ageOption: "", blsCertType: "", instructorCandidateType: "" });
    }
  };

  // Clear any saved instructor info from localStorage (removed auto-fill feature)
  useEffect(() => {
    localStorage.removeItem("instructorInfo");
  }, []);

  const handleNext = () => {
    // Opioid only needs instructor name and course type
    if (isOpioid) {
      if (!data.leadInstructorName || !data.courseType) {
        toast.error("Please fill in instructor name");
        return;
      }
    } else {
      if (!data.instructorInitials || !data.instructorNumber || !data.courseType) {
        toast.error("Please fill in all required fields");
        return;
      }
    }
    if (needsAgeOption && !data.ageOption) {
      toast.error("Please select an age option");
      return;
    }
    if (isBLS && !data.blsCertType) {
      toast.error("Please select certification type");
      return;
    }
    if (isInstructorCandidate && !data.instructorCandidateType) {
      toast.error("Please select instructor candidate type");
      return;
    }
    onNext();
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto"
      data-testid="class-setup-page"
    >
      <div className="text-center mb-8">
        <img 
          src={LOGO_URL} 
          alt="Biggest Little CPR" 
          className="logo-image-large mx-auto mb-6"
          data-testid="setup-logo"
        />
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Class Setup
        </h1>
        <p className="text-zinc-400">Enter your class details to get started</p>
      </div>

      <Card className="card-dark p-8">
        <CardContent className="space-y-6 pt-2">
          {/* Instructor Info Row - Hidden for Opioid */}
          {!isOpioid && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="initials" className="text-zinc-400 text-sm font-medium">
                  Instructor Initials *
                </Label>
                <Input
                  id="initials"
                  data-testid="instructor-initials-input"
                  placeholder="JD"
                  value={data.instructorInitials}
                  onChange={(e) => setData({ ...data, instructorInitials: e.target.value.toUpperCase() })}
                  className="input-dark h-12 text-lg uppercase"
                  maxLength={4}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="number" className="text-zinc-400 text-sm font-medium">
                  Instructor Number *
                </Label>
                <Input
                  id="number"
                  data-testid="instructor-number-input"
                  placeholder="12345"
                  value={data.instructorNumber}
                  onChange={(e) => setData({ ...data, instructorNumber: e.target.value })}
                  className="input-dark h-12 text-lg"
                />
              </div>
            </div>
          )}

          {/* Lead Instructor Name */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm font-medium">
              {isOpioid ? "Instructor Name *" : "Lead Instructor Name *"}
            </Label>
            <Input
              placeholder="Enter lead instructor name..."
              value={data.leadInstructorName || ""}
              onChange={(e) => setData({ ...data, leadInstructorName: e.target.value })}
              className="h-12 input-dark"
              data-testid="lead-instructor-name-input"
            />
          </div>

          {/* Course Selection */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm font-medium">
              Course Type *
            </Label>
            <Select
              value={data.courseType}
              onValueChange={handleCourseChange}
            >
              <SelectTrigger 
                className="h-12 input-dark"
                data-testid="course-type-select"
              >
                <SelectValue placeholder="Select a course..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {availableCourses.map((course) => (
                  <SelectItem 
                    key={course} 
                    value={course}
                    className="hover:bg-zinc-800 focus:bg-zinc-800"
                    data-testid={`course-option-${course.replace(/\s/g, '-')}`}
                  >
                    {course}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Age Option (only for courses that need it) */}
          <AnimatePresence>
            {needsAgeOption && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <Label className="text-zinc-400 text-sm font-medium">
                  Age Group *
                </Label>
                <Select
                  value={data.ageOption}
                  onValueChange={(value) => setData({ ...data, ageOption: value })}
                >
                  <SelectTrigger 
                    className="h-12 input-dark border-cyan/30"
                    data-testid="age-option-select"
                  >
                    <SelectValue placeholder="Select age group..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {courseAgeOptions.map((option) => (
                      <SelectItem 
                        key={option} 
                        value={option}
                        className="hover:bg-zinc-800 focus:bg-zinc-800"
                        data-testid={`age-option-${option.replace(/\s/g, '-')}`}
                      >
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-cyan mt-1">
                  This determines which forms will be included
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* BLS Certification Type (only for BLS) */}
          <AnimatePresence>
            {isBLS && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <Label className="text-zinc-400 text-sm font-medium">
                  Certification Type *
                </Label>
                <Select
                  value={data.blsCertType}
                  onValueChange={(value) => setData({ ...data, blsCertType: value })}
                >
                  <SelectTrigger 
                    className="h-12 input-dark border-cyan/30"
                    data-testid="bls-cert-type-select"
                  >
                    <SelectValue placeholder="Select certification type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="Renewal" className="hover:bg-zinc-800 focus:bg-zinc-800">
                      Renewal
                    </SelectItem>
                    <SelectItem value="New Certification" className="hover:bg-zinc-800 focus:bg-zinc-800">
                      New Certification
                    </SelectItem>
                    <SelectItem value="HeartCode BLS" className="hover:bg-zinc-800 focus:bg-zinc-800">
                      HeartCode BLS
                    </SelectItem>
                  </SelectContent>
                </Select>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Instructor Candidate Type (only for Instructor Candidate) */}
          <AnimatePresence>
            {isInstructorCandidate && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <Label className="text-zinc-400 text-sm font-medium">
                  Instructor Candidate Type *
                </Label>
                <Select
                  value={data.instructorCandidateType}
                  onValueChange={(value) => setData({ 
                    ...data, 
                    instructorCandidateType: value,
                    // Auto-enable roster for both BLS and HS Instructor Candidate
                    includeRoster: (value === "BLS Instructor Candidate" || value === "HS Instructor Candidate") ? true : data.includeRoster
                  })}
                >
                  <SelectTrigger 
                    className="h-12 input-dark border-cyan/30"
                    data-testid="instructor-candidate-type-select"
                  >
                    <SelectValue placeholder="Select instructor candidate type..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="BLS Instructor Candidate" className="hover:bg-zinc-800 focus:bg-zinc-800">
                      BLS Instructor Candidate
                    </SelectItem>
                    <SelectItem value="HS Instructor Candidate" className="hover:bg-zinc-800 focus:bg-zinc-800">
                      HS Instructor Candidate
                    </SelectItem>
                  </SelectContent>
                </Select>
                {(data.instructorCandidateType === "BLS Instructor Candidate" || data.instructorCandidateType === "HS Instructor Candidate") && (
                  <p className="text-xs text-cyan mt-1">
                    Instructor Candidate Application form will be included
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Include Class Roster Toggle - Hidden for Opioid and Instructor Candidate */}
          {!isOpioid && data.courseType !== "Instructor Candidate" && (
            <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div>
                <Label className="text-zinc-300 font-medium">
                  {data.includeRoster ? "Testing Sheets + Roster" : "Testing Sheets Only"}
                </Label>
                <p className="text-xs text-zinc-500 mt-1">
                  {data.includeRoster ? "Includes class roster with student tracking" : "Toggle to add class roster"}
                </p>
              </div>
              <Switch
                checked={data.includeRoster || false}
                onCheckedChange={(checked) => setData({ ...data, includeRoster: checked })}
                data-testid="include-roster-toggle"
              />
            </div>
          )}

          {/* Empty Testing Sheets Toggle - Hidden for Opioid */}
          {!isOpioid && (
            <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div>
                <Label className="text-zinc-300 font-medium">
                  Empty Testing Sheets (Not Prechecked)
                </Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Select this to print testing sheets without pre-filled checkmarks for manual completion
                </p>
              </div>
              <Switch
                checked={data.blankPrintableMode || false}
                onCheckedChange={(checked) => setData({ ...data, blankPrintableMode: checked })}
                data-testid="blank-printable-toggle"
              />
            </div>
          )}

          {/* Next Button */}
          <div className="pt-4">
            <Button
              onClick={handleNext}
              className="w-full h-14 btn-primary-glow text-lg font-semibold"
              data-testid="next-to-students-btn"
            >
              {isOpioid ? "Continue to Students" : 
               data.courseType === "Instructor Candidate" ? "Continue to Class Roster" :
               (data.includeRoster ? "Continue to Class Roster" : "Continue to Students")}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

    </motion.div>
  );
};

// Step 1.5: Class Roster (optional, only when includeRoster is true)
const ClassRoster = ({ data, setData, onNext, onBack }) => {
  // Initialize toggle state from existing data
  const existingInstructors = data.assistingInstructors || [];
  const [hasAssistingInstructors, setHasAssistingInstructors] = useState(existingInstructors.length > 0);
  const [numAssistingInstructors, setNumAssistingInstructors] = useState(existingInstructors.length || 0);

  // Initialize assisting instructors array if needed
  useEffect(() => {
    if (hasAssistingInstructors && numAssistingInstructors > 0) {
      const currentInstructors = data.assistingInstructors || [];
      if (currentInstructors.length !== numAssistingInstructors) {
        const newInstructors = Array(numAssistingInstructors).fill(null).map((_, i) => 
          currentInstructors[i] || { name: "", initials: "", number: "" }
        );
        setData({ ...data, assistingInstructors: newInstructors });
      }
    } else if (!hasAssistingInstructors) {
      // Clear assisting instructors when toggle is turned off
      if (data.assistingInstructors && data.assistingInstructors.length > 0) {
        setData({ ...data, assistingInstructors: [] });
      }
    }
  }, [hasAssistingInstructors, numAssistingInstructors]);

  // Auto-calculate total hours when start/end times change
  useEffect(() => {
    // Check if we have all required time fields
    if (data.startHour && data.endHour) {
      // Parse start time to 24-hour format
      let startHour = parseInt(data.startHour, 10);
      const startMinute = parseInt(data.startMinute, 10) || 0;
      const startPeriod = data.startPeriod || "AM";
      
      // Parse end time to 24-hour format
      let endHour = parseInt(data.endHour, 10);
      const endMinute = parseInt(data.endMinute, 10) || 0;
      const endPeriod = data.endPeriod || "PM";
      
      // Convert to 24-hour format
      if (startPeriod === "PM" && startHour !== 12) startHour += 12;
      if (startPeriod === "AM" && startHour === 12) startHour = 0;
      if (endPeriod === "PM" && endHour !== 12) endHour += 12;
      if (endPeriod === "AM" && endHour === 12) endHour = 0;
      
      let totalMinutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
      
      // Handle overnight (if end time is before start time)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }
      
      // Format as "X hours Y minutes"
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      let formattedTime = "";
      if (hours > 0 && minutes > 0) {
        formattedTime = `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else if (hours > 0) {
        formattedTime = `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (minutes > 0) {
        formattedTime = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else {
        formattedTime = "0 minutes";
      }
      
      // Also build the combined time strings for export
      const padZero = (n) => String(n).padStart(2, '0');
      const startTimeStr = `${data.startHour}:${padZero(startMinute)} ${startPeriod}`;
      const endTimeStr = `${data.endHour}:${padZero(endMinute)} ${endPeriod}`;
      
      if (data.totalHours !== formattedTime || data.courseStartTime !== startTimeStr || data.courseEndTime !== endTimeStr) {
        setData({ 
          ...data, 
          totalHours: formattedTime,
          courseStartTime: startTimeStr,
          courseEndTime: endTimeStr
        });
      }
    }
  }, [data.startHour, data.startMinute, data.startPeriod, data.endHour, data.endMinute, data.endPeriod]);

  const updateAssistingInstructor = (index, field, value) => {
    const newInstructors = [...(data.assistingInstructors || [])];
    if (!newInstructors[index]) {
      newInstructors[index] = { name: "", initials: "", number: "" };
    }
    newInstructors[index] = { ...newInstructors[index], [field]: value };
    setData({ ...data, assistingInstructors: newInstructors });
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto"
      data-testid="class-roster-page"
    >
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Class Roster Details
        </h1>
        <p className="text-zinc-400">Optional additional class information</p>
      </div>

      <Card className="card-dark p-8">
        <CardContent className="space-y-6 pt-2">
          {/* Course Location */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm font-medium">Course Location <span className="text-red-500">*</span></Label>
            <Input
              placeholder="Enter course location"
              value={data.courseLocation || ""}
              onChange={(e) => setData({ ...data, courseLocation: e.target.value })}
              className={`input-dark h-12 ${!data.courseLocation ? 'border-red-500/50' : ''}`}
              data-testid="course-location-input"
            />
          </div>

          {/* Time Row - Time Selectors */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm font-medium">Course Start Time <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="HH"
                  maxLength={2}
                  value={data.startHour || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setData({ ...data, startHour: val });
                  }}
                  className="input-dark h-12 w-16 text-center"
                  data-testid="start-hour-input"
                />
                <span className="text-zinc-400 text-xl">:</span>
                <Input
                  type="text"
                  placeholder="MM"
                  maxLength={2}
                  value={data.startMinute || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setData({ ...data, startMinute: val });
                  }}
                  className="input-dark h-12 w-16 text-center"
                  data-testid="start-minute-input"
                />
                <Select
                  value={data.startPeriod || "AM"}
                  onValueChange={(value) => setData({ ...data, startPeriod: value })}
                >
                  <SelectTrigger className="input-dark h-12 w-20" data-testid="start-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm font-medium">Course End Time <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  placeholder="HH"
                  maxLength={2}
                  value={data.endHour || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setData({ ...data, endHour: val });
                  }}
                  className="input-dark h-12 w-16 text-center"
                  data-testid="end-hour-input"
                />
                <span className="text-zinc-400 text-xl">:</span>
                <Input
                  type="text"
                  placeholder="MM"
                  maxLength={2}
                  value={data.endMinute || ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setData({ ...data, endMinute: val });
                  }}
                  className="input-dark h-12 w-16 text-center"
                  data-testid="end-minute-input"
                />
                <Select
                  value={data.endPeriod || "PM"}
                  onValueChange={(value) => setData({ ...data, endPeriod: value })}
                >
                  <SelectTrigger className="input-dark h-12 w-20" data-testid="end-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Total Hours - Auto-calculated */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm font-medium">Time of Course</Label>
            <Input
              type="text"
              placeholder="Auto-calculated from times"
              value={data.totalHours || ""}
              readOnly
              className="input-dark h-12 bg-zinc-900/50 text-zinc-300"
              data-testid="total-hours-input"
            />
            <p className="text-xs text-zinc-500">Automatically calculated from start and end times</p>
          </div>

          {/* Student to Manikin Ratio */}
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm font-medium">Student to Manikin Ratio <span className="text-red-500">*</span></Label>
            <Select
              value={data.manikinRatio || ""}
              onValueChange={(value) => setData({ ...data, manikinRatio: value })}
            >
              <SelectTrigger className="h-12 input-dark" data-testid="manikin-ratio-select">
                <SelectValue placeholder="Select ratio..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="1:1" className="hover:bg-zinc-800">1:1</SelectItem>
                <SelectItem value="2:1" className="hover:bg-zinc-800">2:1</SelectItem>
                <SelectItem value="3:1" className="hover:bg-zinc-800">3:1</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Exam Issued? - Only for HS courses with exam fields */}
          {["HS CPR/AED", "HS FA CPR/AED", "HS Pediatric First Aid CPR/AED", "HS Total", "HS K-12"].includes(data.courseType) && (
            <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div>
                <Label className="text-zinc-300 font-medium">Exam Issued?</Label>
                <p className="text-xs text-zinc-500 mt-1">Select if an exam was administered for this course</p>
              </div>
              <Switch
                checked={data.examGiven || false}
                onCheckedChange={(checked) => setData({ ...data, examGiven: checked })}
                data-testid="exam-given-toggle"
              />
            </div>
          )}

          {/* Assisting Instructors Question */}
          <div className="space-y-4 p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-300 font-medium">Add Assisting Instructors</Label>
              <Switch
                checked={hasAssistingInstructors}
                onCheckedChange={(checked) => {
                  setHasAssistingInstructors(checked);
                  if (!checked) {
                    setNumAssistingInstructors(0);
                    setData({ ...data, assistingInstructors: [] });
                  }
                }}
                data-testid="assisting-instructors-toggle"
              />
            </div>

            <AnimatePresence>
              {hasAssistingInstructors && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm font-medium">Number of Assisting Instructors</Label>
                    <Select
                      value={numAssistingInstructors.toString()}
                      onValueChange={(value) => setNumAssistingInstructors(parseInt(value))}
                    >
                      <SelectTrigger className="h-12 input-dark" data-testid="num-assisting-select">
                        <SelectValue placeholder="Select number..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <SelectItem key={num} value={num.toString()} className="hover:bg-zinc-800">
                            {num}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Dynamic instructor name, initials, and number fields */}
                  {numAssistingInstructors > 0 && (
                    <div className="space-y-3">
                      {Array.from({ length: numAssistingInstructors }).map((_, index) => (
                        <div key={index} className="space-y-2 p-3 bg-zinc-900/30 rounded-lg border border-zinc-800">
                          <Label className="text-zinc-400 text-xs font-medium">Assisting Instructor {index + 1}</Label>
                          <div className="flex gap-3">
                            <div className="flex-1">
                              <Input
                                placeholder="Name"
                                value={(data.assistingInstructors || [])[index]?.name || ""}
                                onChange={(e) => updateAssistingInstructor(index, 'name', e.target.value)}
                                className="input-dark h-10"
                                data-testid={`assisting-instructor-name-${index + 1}`}
                              />
                            </div>
                            <div className="w-20">
                              <Input
                                placeholder="Initials"
                                value={(data.assistingInstructors || [])[index]?.initials || ""}
                                onChange={(e) => updateAssistingInstructor(index, 'initials', e.target.value.toUpperCase())}
                                className="input-dark h-10 uppercase"
                                maxLength={4}
                                data-testid={`assisting-instructor-initials-${index + 1}`}
                              />
                            </div>
                            <div className="w-28">
                              <Input
                                placeholder="Instructor #"
                                value={(data.assistingInstructors || [])[index]?.number || ""}
                                onChange={(e) => updateAssistingInstructor(index, 'number', e.target.value)}
                                className="input-dark h-10"
                                data-testid={`assisting-instructor-number-${index + 1}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              variant="outline"
              onClick={onBack}
              className="h-14 border-zinc-700 hover:bg-zinc-900 px-6"
              data-testid="back-to-setup-btn"
            >
              <ChevronLeft className="mr-2 w-5 h-5" />
              Back
            </Button>
            <Button
              onClick={() => {
                // Validate required fields
                if (!data.courseLocation?.trim()) {
                  toast.error("Please enter course location");
                  return;
                }
                onNext();
              }}
              className="flex-1 h-14 btn-primary-glow text-lg font-semibold"
              data-testid="continue-to-students-btn"
            >
              {data.courseType === "Instructor Candidate" || data.includeRoster ? "Continue to TC Info" : "Continue to Students"}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Step 2.5: TC Info (only for Instructor Candidate course type)
const TCInfo = ({ data, setData, onNext, onBack }) => {
  const [tcProfiles, setTcProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Load saved TC profiles on mount
  useEffect(() => {
    const loadTcProfiles = async () => {
      const token = localStorage.getItem('cpr_auth_token');
      if (!token) {
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/user/tc-profiles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          setTcProfiles(response.data.tc_profiles || []);
        }
      } catch (err) {
        console.log("Could not load TC profiles:", err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadTcProfiles();
  }, []);

  const handleChange = (field, value) => {
    setData({
      ...data,
      instructorCandidateInfo: {
        ...data.instructorCandidateInfo,
        [field]: value
      }
    });
  };

  const handleSelectProfile = (profileId) => {
    setSelectedProfileId(profileId);
    setIsAddingNew(false);
    
    if (profileId === "add_new") {
      // Clear fields and show add new form
      setIsAddingNew(true);
      setNewProfileName("");
      setData(prev => ({
        ...prev,
        instructorCandidateInfo: {
          ...prev.instructorCandidateInfo,
          tcName: "",
          tcId: "",
          trainingSite: "",
          tcAddress: "",
          tcCityStateZip: ""
        }
      }));
    } else if (profileId) {
      // Load selected profile
      const profile = tcProfiles.find(p => p.id === profileId);
      if (profile) {
        setData(prev => ({
          ...prev,
          instructorCandidateInfo: {
            ...prev.instructorCandidateInfo,
            tcName: profile.tc_name,
            tcId: profile.tc_id,
            trainingSite: profile.training_site,
            tcAddress: profile.tc_address,
            tcCityStateZip: profile.tc_city_state_zip
          }
        }));
      }
    } else {
      // Clear selection
      setData(prev => ({
        ...prev,
        instructorCandidateInfo: {
          ...prev.instructorCandidateInfo,
          tcName: "",
          tcId: "",
          trainingSite: "",
          tcAddress: "",
          tcCityStateZip: ""
        }
      }));
    }
  };

  const handleSaveNewProfile = async () => {
    if (!newProfileName.trim()) {
      toast.error("Please enter a name for this TC profile");
      return;
    }
    
    const candidateInfo = data.instructorCandidateInfo || {};
    
    if (!candidateInfo.tcName?.trim() || !candidateInfo.tcId?.trim() || 
        !candidateInfo.tcAddress?.trim() || !candidateInfo.tcCityStateZip?.trim()) {
      toast.error("Please fill in all required TC fields before saving");
      return;
    }
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.post(`${API}/user/tc-profiles`, {
        name: newProfileName.trim(),
        tc_name: candidateInfo.tcName,
        tc_id: candidateInfo.tcId,
        training_site: candidateInfo.trainingSite || "",
        tc_address: candidateInfo.tcAddress,
        tc_city_state_zip: candidateInfo.tcCityStateZip
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const newProfile = response.data.profile;
        setTcProfiles(prev => [...prev, newProfile]);
        setSelectedProfileId(newProfile.id);
        setIsAddingNew(false);
        setNewProfileName("");
        toast.success("TC profile saved!");
      }
    } catch (err) {
      toast.error("Failed to save TC profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteProfile = async (profileId, e) => {
    e.stopPropagation();
    
    if (!window.confirm("Are you sure you want to delete this TC profile?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem('cpr_auth_token');
      const response = await axios.delete(`${API}/user/tc-profiles/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setTcProfiles(prev => prev.filter(p => p.id !== profileId));
        if (selectedProfileId === profileId) {
          setSelectedProfileId("");
          setData(prev => ({
            ...prev,
            instructorCandidateInfo: {
              ...prev.instructorCandidateInfo,
              tcName: "",
              tcId: "",
              trainingSite: "",
              tcAddress: "",
              tcCityStateZip: ""
            }
          }));
        }
        toast.success("TC profile deleted");
      }
    } catch (err) {
      toast.error("Failed to delete TC profile");
    }
  };
  
  const candidateInfo = data.instructorCandidateInfo || {};
  const hasSelectedOrAdding = selectedProfileId || isAddingNew;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <Card className="card-dark">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-2xl">
            <FileText className="w-8 h-8 text-cyan" />
            TC Info
          </CardTitle>
          <p className="text-zinc-400">
            Select a saved Training Center or add a new one
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* TC Profile Selector */}
          <div>
            <Label>Registered TCs <span className="text-red-500">*</span></Label>
            <div className="mt-2 space-y-2">
              {isLoading ? (
                <div className="flex items-center gap-2 text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading saved TCs...
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Saved TC Profiles */}
                  {tcProfiles.map(profile => (
                    <div
                      key={profile.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedProfileId === profile.id 
                          ? 'border-cyan-500 bg-cyan-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/50'
                      }`}
                      onClick={() => handleSelectProfile(profile.id)}
                      data-testid={`tc-profile-${profile.id}`}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-white">{profile.name}</div>
                        <div className="text-sm text-zinc-400">{profile.tc_name} ({profile.tc_id})</div>
                      </div>
                      <button
                        onClick={(e) => handleDeleteProfile(profile.id, e)}
                        className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        data-testid={`delete-tc-${profile.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add New TC Option */}
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isAddingNew 
                        ? 'border-cyan-500 bg-cyan-500/10' 
                        : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/50'
                    }`}
                    onClick={() => handleSelectProfile("add_new")}
                    data-testid="add-new-tc-btn"
                  >
                    <Plus className="w-5 h-5 text-cyan-400" />
                    <span className="text-cyan-400 font-medium">Add New TC</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* TC Info Form - Only show when a profile is selected or adding new */}
          {hasSelectedOrAdding && (
            <>
              {/* Save Profile Name - Only for new profiles */}
              {isAddingNew && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                  <Label>Profile Name <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="e.g., Main Training Center"
                      value={newProfileName}
                      onChange={(e) => setNewProfileName(e.target.value)}
                      className="input-dark flex-1"
                      data-testid="new-profile-name"
                    />
                    <Button
                      onClick={handleSaveNewProfile}
                      disabled={isSaving}
                      className="btn-primary-glow"
                      data-testid="save-tc-profile-btn"
                    >
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save TC"}
                    </Button>
                  </div>
                  <p className="text-xs text-cyan-400 mt-2">Fill in the fields below, then click "Save TC" to add to your list</p>
                </div>
              )}

              {/* TC Info Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>TC Name <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="Training Center name"
                    value={candidateInfo.tcName || ""}
                    onChange={(e) => handleChange('tcName', e.target.value)}
                    className={`input-dark mt-1 ${!candidateInfo.tcName ? 'border-red-500/50' : ''}`}
                    data-testid="tc-name"
                  />
                </div>
                <div>
                  <Label>TC ID <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="TC ID"
                    value={candidateInfo.tcId || ""}
                    onChange={(e) => handleChange('tcId', e.target.value)}
                    className={`input-dark mt-1 ${!candidateInfo.tcId ? 'border-red-500/50' : ''}`}
                    data-testid="tc-id"
                  />
                </div>
              </div>
              
              {/* Training Center Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Training Center Address <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="Street address"
                    value={candidateInfo.tcAddress || ""}
                    onChange={(e) => handleChange('tcAddress', e.target.value)}
                    className={`input-dark mt-1 ${!candidateInfo.tcAddress ? 'border-red-500/50' : ''}`}
                    data-testid="tc-address"
                  />
                </div>
                <div>
                  <Label>Training Center City, State, Zip <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="City, State ZIP"
                    value={candidateInfo.tcCityStateZip || ""}
                    onChange={(e) => handleChange('tcCityStateZip', e.target.value)}
                    className={`input-dark mt-1 ${!candidateInfo.tcCityStateZip ? 'border-red-500/50' : ''}`}
                    data-testid="tc-city-state-zip"
                  />
                </div>
              </div>
              
              {/* Training Site - Optional */}
              <div>
                <Label>Training Site</Label>
                <Input
                  placeholder="Training Site name"
                  value={candidateInfo.trainingSite || ""}
                  onChange={(e) => handleChange('trainingSite', e.target.value)}
                  className="input-dark mt-1"
                  data-testid="training-site"
                />
              </div>
              
              {/* Date of Signature - Only for IC courses */}
              {data.courseType === "Instructor Candidate" && (
                <div>
                  <Label>Date of Signature <span className="text-red-500">*</span></Label>
                  <Input
                    type="date"
                    value={candidateInfo.signatureDate || ""}
                    onChange={(e) => handleChange('signatureDate', e.target.value)}
                    className={`input-dark mt-1 max-w-xs ${!candidateInfo.signatureDate ? 'border-red-500/50' : ''}`}
                    data-testid="signature-date"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Date for candidate and instructor signatures (applied to all candidates)</p>
                </div>
              )}
            </>
          )}
          
          {/* Navigation */}
          <div className="flex justify-between pt-6">
            <Button
              variant="outline"
              onClick={onBack}
              className="border-zinc-700 hover:bg-zinc-900"
            >
              <ChevronLeft className="mr-2 w-4 h-4" />
              Back
            </Button>
            <Button
              onClick={() => {
                if (!hasSelectedOrAdding) {
                  toast.error("Please select a TC or add a new one");
                  return;
                }
                // Validate required TC Info fields
                if (!candidateInfo.tcName?.trim()) {
                  toast.error("Please enter TC Name");
                  return;
                }
                if (!candidateInfo.tcId?.trim()) {
                  toast.error("Please enter TC ID");
                  return;
                }
                if (!candidateInfo.tcAddress?.trim()) {
                  toast.error("Please enter Training Center Address");
                  return;
                }
                if (!candidateInfo.tcCityStateZip?.trim()) {
                  toast.error("Please enter Training Center City, State, Zip");
                  return;
                }
                // Training Site is optional - no validation needed
                // Date of Signature only required for IC courses
                if (data.courseType === "Instructor Candidate" && !candidateInfo.signatureDate) {
                  toast.error("Please enter Date of Signature");
                  return;
                }
                onNext();
              }}
              className="btn-primary-glow"
              data-testid="continue-to-students-ic"
            >
              Continue to Students
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Step 2: Student Entry
const StudentEntry = ({ data, setData, onNext, onBack }) => {
  const [newStudentName, setNewStudentName] = useState("");
  const [newStudentEmail, setNewStudentEmail] = useState("");
  const [newStudentPhone, setNewStudentPhone] = useState("");
  const [newStudentStatus, setNewStudentStatus] = useState("");
  const [newStudentRemedDate, setNewStudentRemedDate] = useState("");
  const [newStudentAddress, setNewStudentAddress] = useState("");
  const [newStudentCity, setNewStudentCity] = useState("");
  const [newStudentState, setNewStudentState] = useState("");
  const [newStudentZip, setNewStudentZip] = useState("");
  const [newStudentScore, setNewStudentScore] = useState("");  // BLS score field
  const [newStudentInstructor, setNewStudentInstructor] = useState("");  // Selected instructor for this student
  const [isImporting, setIsImporting] = useState(false);
  const [showEnrollwareHelp, setShowEnrollwareHelp] = useState(false);
  const [showMaxStudentsWarning, setShowMaxStudentsWarning] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  // IC-specific state for new student form
  const [newICData, setNewICData] = useState({
    providerCardRenewalDate: "",
    mailingAddress: "",
    city: "",
    state: "",
    zipCode: "",
    candidateInstructorId: "",
    candidateRenewalDate: "",
    candidateSignature: null
  });
  const [icAgreed, setIcAgreed] = useState(false);
  const icSignatureRef = useRef(null);
  
  const MAX_STUDENTS = 20;
  // For Opioid course, use simple mode (name only) even if includeRoster is true
  const isOpioid = data.courseType === "Opioid Awareness from BLCPR";
  const isRosterMode = data.includeRoster && !isOpioid;
  const isInstructorCandidate = data.courseType === "Instructor Candidate";
  const isBLS = data.courseType === "Basic Life Support (BLS)";
  const isHeartsaver = data.courseType?.startsWith("HS ");
  const showScoreField = isBLS || (isHeartsaver && data.examGiven);
  
  // Check if there are assisting instructors (to show instructor selector per student)
  const hasAssistingInstructors = (data.assistingInstructors || []).length > 0;
  
  // Build list of all instructors (lead + assisting)
  const allInstructors = [
    { 
      id: 'lead', 
      name: data.leadInstructorName || 'Lead Instructor', 
      initials: data.instructorInitials || '', 
      number: data.instructorNumber || '' 
    },
    ...(data.assistingInstructors || []).map((inst, idx) => ({
      id: `assist-${idx}`,
      name: inst?.name || `Instructor ${idx + 2}`,
      initials: inst?.initials || '',
      number: inst?.number || ''
    }))
  ];

  const clearNewICSignature = () => {
    if (icSignatureRef.current) {
      icSignatureRef.current.clear();
      setNewICData({ ...newICData, candidateSignature: null });
    }
  };

  const saveNewICSignature = () => {
    if (icSignatureRef.current && !icSignatureRef.current.isEmpty()) {
      const dataUrl = icSignatureRef.current.toDataURL('image/png');
      setNewICData({ ...newICData, candidateSignature: dataUrl });
    }
  };

  const addStudent = () => {
    // Check if we've reached the maximum number of students
    if (data.students.length >= MAX_STUDENTS) {
      setShowMaxStudentsWarning(true);
      return;
    }
    
    if (!newStudentName.trim()) {
      toast.error("Please enter a student name");
      return;
    }
    
    // Validate mandatory fields in roster mode
    if (isRosterMode && !isInstructorCandidate) {
      if (!newStudentEmail.trim()) {
        toast.error("Please enter student email");
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newStudentEmail.trim())) {
        toast.error("Please enter a valid email address");
        return;
      }
      if (!newStudentPhone.trim()) {
        toast.error("Please enter student phone number");
        return;
      }
      // Validate phone - must be 10 digits (strip non-digits first)
      const phoneDigits = newStudentPhone.replace(/\D/g, '');
      if (phoneDigits.length !== 10) {
        toast.error("Please enter a valid 10-digit phone number");
        return;
      }
      if (!newStudentStatus) {
        toast.error("Please select a status");
        return;
      }
    }
    
    // Validate optional email format for Opioid if provided
    if (isOpioid && newStudentEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newStudentEmail.trim())) {
        toast.error("Please enter a valid email address");
        return;
      }
    }
    
    // Validate IC-specific mandatory fields
    if (isInstructorCandidate) {
      if (!newICData.providerCardRenewalDate) {
        toast.error("Please enter Provider Card Renewal Date");
        return;
      }
      if (!newICData.mailingAddress?.trim()) {
        toast.error("Please enter Mailing Address");
        return;
      }
      if (!newICData.city?.trim()) {
        toast.error("Please enter City");
        return;
      }
      if (!newICData.state?.trim()) {
        toast.error("Please enter State");
        return;
      }
      if (!newICData.zipCode?.trim()) {
        toast.error("Please enter Zip Code");
        return;
      }
      if (!newStudentPhone?.trim()) {
        toast.error("Please enter Phone Number");
        return;
      }
      if (!newStudentEmail?.trim()) {
        toast.error("Please enter Email");
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newStudentEmail.trim())) {
        toast.error("Please enter a valid email address");
        return;
      }
    }
    
    const newStudent = { name: newStudentName.trim() };
    
    // Add instructor assignment if there are assisting instructors
    if (hasAssistingInstructors && newStudentInstructor) {
      const selectedInstructor = allInstructors.find(i => i.id === newStudentInstructor);
      if (selectedInstructor) {
        newStudent.assignedInstructor = {
          id: selectedInstructor.id,
          name: selectedInstructor.name,
          initials: selectedInstructor.initials,
          number: selectedInstructor.number
        };
      }
    }
    
    if (isRosterMode && !isInstructorCandidate) {
      newStudent.email = newStudentEmail.trim();
      // Format phone as (XXX) XXX-XXXX
      const phoneDigits = newStudentPhone.replace(/\D/g, '');
      newStudent.phone = `(${phoneDigits.slice(0,3)}) ${phoneDigits.slice(3,6)}-${phoneDigits.slice(6)}`;
      newStudent.status = newStudentStatus;
      // Add address fields
      if (newStudentAddress.trim()) {
        newStudent.address = newStudentAddress.trim();
      }
      if (newStudentCity.trim()) {
        newStudent.city = newStudentCity.trim();
      }
      if (newStudentState.trim()) {
        newStudent.state = newStudentState.trim();
      }
      if (newStudentZip.trim()) {
        newStudent.zip = newStudentZip.trim();
      }
      // Add score for BLS or Heartsaver with Exam Given (optional)
      if (showScoreField && newStudentScore.trim()) {
        newStudent.score = newStudentScore.trim();
      }
      if ((newStudentStatus === "REM/PASS" || newStudentStatus === "REM/FAIL") && newStudentRemedDate) {
        newStudent.remediationDate = newStudentRemedDate;
      }
    } else if (isOpioid && newStudentEmail.trim()) {
      // Add optional email for Opioid course
      newStudent.email = newStudentEmail.trim();
    } else if (isInstructorCandidate) {
      // Add IC-specific fields
      newStudent.email = newStudentEmail.trim();
      if (newStudentPhone.trim()) {
        const phoneDigits = newStudentPhone.replace(/\D/g, '');
        if (phoneDigits.length === 10) {
          newStudent.phone = `(${phoneDigits.slice(0,3)}) ${phoneDigits.slice(3,6)}-${phoneDigits.slice(6)}`;
        } else {
          newStudent.phone = newStudentPhone.trim();
        }
      }
      newStudent.providerCardRenewalDate = newICData.providerCardRenewalDate;
      newStudent.mailingAddress = newICData.mailingAddress;
      newStudent.city = newICData.city;
      newStudent.state = newICData.state;
      newStudent.zipCode = newICData.zipCode;
      newStudent.candidateInstructorId = newICData.candidateInstructorId;
      newStudent.candidateRenewalDate = newICData.candidateRenewalDate;
      newStudent.candidateSignature = newICData.candidateSignature;
      newStudent.status = "Pass"; // Default status for IC
    }
    
    setData({
      ...data,
      students: [...data.students, newStudent]
    });
    setNewStudentName("");
    setNewStudentEmail("");
    setNewStudentPhone("");
    setNewStudentStatus("");
    setNewStudentRemedDate("");
    setNewStudentAddress("");
    setNewStudentCity("");
    setNewStudentState("");
    setNewStudentZip("");
    setNewStudentScore("");  // Reset score field
    setNewStudentInstructor("");  // Reset instructor selector
    // Reset IC fields
    setNewICData({
      providerCardRenewalDate: "",
      mailingAddress: "",
      city: "",
      state: "",
      zipCode: "",
      candidateInstructorId: "",
      candidateRenewalDate: "",
      candidateSignature: null
    });
    setIcAgreed(false);
    if (icSignatureRef.current) {
      icSignatureRef.current.clear();
    }
    toast.success("Student added!");
  };

  const removeStudent = (index) => {
    setData({
      ...data,
      students: data.students.filter((_, i) => i !== index)
    });
    toast.success("Student removed");
  };

  const markAllPass = () => {
    if (data.students.length === 0) {
      toast.error("No students to mark");
      return;
    }
    const updatedStudents = data.students.map(student => ({
      ...student,
      status: "Pass"
    }));
    setData({ ...data, students: updatedStudents });
    toast.success(`Marked ${data.students.length} student(s) as Pass`);
  };
  
  const updateStudent = (index, field, value) => {
    const updatedStudents = [...data.students];
    updatedStudents[index] = { ...updatedStudents[index], [field]: value };
    setData({ ...data, students: updatedStudents });
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file || isImporting) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      event.target.value = '';
      return;
    }

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import-students`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.students) {
        const currentCount = data.students.length;
        const importedStudents = response.data.students;
        const availableSlots = MAX_STUDENTS - currentCount;
        
        // Check if importing would exceed max students
        if (importedStudents.length > availableSlots) {
          const studentsToAdd = importedStudents.slice(0, availableSlots);
          const skippedCount = importedStudents.length - availableSlots;
          
          const updates = {
            ...data,
            students: [...data.students, ...studentsToAdd]
          };
          
          if (response.data.class_date) {
            updates.classDate = response.data.class_date;
          }
          
          setData(updates);
          
          if (availableSlots > 0) {
            toast.warning(`Added ${studentsToAdd.length} students. ${skippedCount} students were skipped (max ${MAX_STUDENTS} students allowed).`);
          }
          setShowMaxStudentsWarning(true);
        } else {
          const updates = {
            ...data,
            students: [...data.students, ...importedStudents]
          };
          
          // Auto-fill date from A2 if provided
          if (response.data.class_date) {
            updates.classDate = response.data.class_date;
            toast.success(`Imported ${response.data.count} students! Date auto-filled from file.`);
          } else {
            toast.success(`Imported ${response.data.count} students!`);
          }
          
          setData(updates);
        }
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import students");
    } finally {
      setIsImporting(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const handleNext = () => {
    if (data.students.length === 0) {
      toast.error("Please add at least one student");
      return;
    }
    
    // Validate date of class
    if (!data.classDate) {
      toast.error("Please enter the Date of Class");
      return;
    }
    
    // In roster mode (non-IC), validate all students have required fields
    if (isRosterMode && !isInstructorCandidate) {
      const incompleteStudents = data.students.filter(s => !s.status || !s.email || !s.phone);
      if (incompleteStudents.length > 0) {
        toast.error(`Please complete all required fields (email, phone, status) for ${incompleteStudents.length} student(s)`);
        return;
      }
      
      // Validate email format for all students
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = data.students.filter(s => !emailRegex.test(s.email));
      if (invalidEmails.length > 0) {
        toast.error(`Invalid email format for ${invalidEmails.length} student(s)`);
        return;
      }
      
      // Validate phone format for all students (10 digits)
      const invalidPhones = data.students.filter(s => {
        const digits = s.phone.replace(/\D/g, '');
        return digits.length !== 10;
      });
      if (invalidPhones.length > 0) {
        toast.error(`Invalid phone number for ${invalidPhones.length} student(s). Must be 10 digits.`);
        return;
      }
    }
    
    // Validate IC-specific required fields for Instructor Candidate courses
    if (isInstructorCandidate) {
      const incompleteStudents = data.students.filter(s => 
        !s.providerCardRenewalDate || 
        !s.mailingAddress?.trim() || 
        !s.city?.trim() || 
        !s.state?.trim() || 
        !s.zipCode?.trim() || 
        !s.phone?.trim() || 
        !s.email?.trim()
      );
      if (incompleteStudents.length > 0) {
        toast.error(`Please complete all required IC fields for ${incompleteStudents.length} student(s): Provider Card Renewal Date, Address, City, State, Zip, Phone, Email`);
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = data.students.filter(s => !emailRegex.test(s.email));
      if (invalidEmails.length > 0) {
        toast.error(`Invalid email format for ${invalidEmails.length} student(s)`);
        return;
      }
    }
    
    onNext();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      addStudent();
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="max-w-3xl mx-auto"
      data-testid="student-entry-page"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Student Entry
          </h1>
          <p className="text-zinc-400">Add students for {data.courseType}</p>
        </div>
        <div className="badge-cyan">
          <Users className="w-4 h-4 inline mr-2" />
          {data.students.length} student{data.students.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Add Student Input */}
      <Card className="card-dark p-6 mb-6">
        {/* Student Entry Form */}
        {isInstructorCandidate ? (
          /* IC mode - name + contact + IC application fields + signature */
          <div className="space-y-4 mb-4 p-4 bg-zinc-900/30 rounded-lg border border-zinc-800">
            <h3 className="text-lg font-semibold text-cyan mb-2">Add Instructor Candidate</h3>
            
            {/* Name */}
            <div className="space-y-1">
              <Label className="text-zinc-400 text-sm font-medium">Candidate Name <span className="text-red-500">*</span></Label>
              <Input
                data-testid="student-name-input"
                placeholder="Enter candidate name..."
                value={newStudentName}
                onChange={(e) => setNewStudentName(e.target.value)}
                className="input-dark h-12"
              />
            </div>
            
            {/* Provider Card Renewal Date */}
            <div className="space-y-1">
              <Label className="text-zinc-400 text-sm font-medium">Provider Card Renewal Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={newICData.providerCardRenewalDate}
                onChange={(e) => setNewICData({ ...newICData, providerCardRenewalDate: e.target.value })}
                className="input-dark h-12 max-w-xs"
                data-testid="ic-provider-renewal-date"
              />
            </div>
            
            {/* Address */}
            <div className="space-y-1">
              <Label className="text-zinc-400 text-sm font-medium">Mailing Address <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Street address"
                value={newICData.mailingAddress}
                onChange={(e) => setNewICData({ ...newICData, mailingAddress: e.target.value })}
                className="input-dark h-12"
                data-testid="ic-mailing-address"
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">City <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="City"
                  value={newICData.city}
                  onChange={(e) => setNewICData({ ...newICData, city: e.target.value })}
                  className="input-dark h-12"
                  data-testid="ic-city"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">State <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="State"
                  value={newICData.state}
                  onChange={(e) => setNewICData({ ...newICData, state: e.target.value })}
                  className="input-dark h-12"
                  data-testid="ic-state"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Zip Code <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="Zip"
                  value={newICData.zipCode}
                  onChange={(e) => setNewICData({ ...newICData, zipCode: e.target.value })}
                  className="input-dark h-12"
                  data-testid="ic-zip"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Phone <span className="text-red-500">*</span></Label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={newStudentPhone}
                  onChange={(e) => setNewStudentPhone(e.target.value)}
                  className="input-dark h-12"
                  data-testid="ic-phone"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  placeholder="candidate@email.com"
                  value={newStudentEmail}
                  onChange={(e) => setNewStudentEmail(e.target.value)}
                  className="input-dark h-12"
                  data-testid="ic-email"
                />
              </div>
            </div>
            
            {/* Instructor Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Candidate Instructor ID</Label>
                <Input
                  placeholder="Instructor ID #"
                  value={newICData.candidateInstructorId}
                  onChange={(e) => setNewICData({ ...newICData, candidateInstructorId: e.target.value })}
                  className="input-dark h-12"
                  data-testid="ic-instructor-id"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Renewal Date</Label>
                <Input
                  type="date"
                  value={newICData.candidateRenewalDate}
                  onChange={(e) => setNewICData({ ...newICData, candidateRenewalDate: e.target.value })}
                  className="input-dark h-12"
                  data-testid="ic-renewal-date"
                />
              </div>
            </div>
            
            {/* Instructor Commitment */}
            <div className="space-y-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-700">
              <Label className="text-zinc-300 text-sm font-semibold">Instructor Commitment:</Label>
              <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-400">
                <li>Teach at LEAST 4 courses in 2 years in accordance with the guidelines of AHA</li>
                <li>Maintain a current provider card</li>
                <li>Strengthen and support the chain of survival and the mission of the AHA in my community</li>
                <li>Conduct myself in accordance with the ECC Leadership Code Of Conduct</li>
                <li>Avoid any perception of conflict of interest in accordance with the AHA Statement of Conflict of Interest</li>
              </ol>
              <div className="pt-2">
                <Button
                  type="button"
                  onClick={() => setIcAgreed(!icAgreed)}
                  className={`w-full ${icAgreed ? 'bg-green-600 hover:bg-green-700' : 'bg-zinc-700 hover:bg-zinc-600'} text-white`}
                  data-testid="ic-agree-btn"
                >
                  {icAgreed ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      I Agree
                    </>
                  ) : (
                    "I Agree"
                  )}
                </Button>
              </div>
            </div>
            
            {/* Candidate Signature - Only enabled after agreeing */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm font-medium">Candidate Signature {!icAgreed && <span className="text-amber-400 text-xs">(Please agree to commitment first)</span>}</Label>
              <div className={`border rounded-lg p-2 bg-white ${!icAgreed ? 'opacity-50 pointer-events-none' : 'border-zinc-700'}`}>
                <SignatureCanvas
                  ref={icSignatureRef}
                  canvasProps={{
                    className: 'signature-canvas w-full h-24',
                    style: { width: '100%', height: '96px' }
                  }}
                  onEnd={saveNewICSignature}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearNewICSignature}
                  className="border-zinc-700"
                  type="button"
                >
                  <Eraser className="w-4 h-4 mr-1" />
                  Clear
                </Button>
                {newICData.candidateSignature && (
                  <span className="text-xs text-green-400 flex items-center">
                    <Check className="w-3 h-3 mr-1" /> Signature saved
                  </span>
                )}
              </div>
            </div>
            
            {/* Add Button */}
            <div className="pt-2">
              <Button
                onClick={addStudent}
                className="btn-primary-glow h-12 px-6 w-full"
                data-testid="add-student-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Candidate
              </Button>
            </div>
          </div>
        ) : !isRosterMode ? (
          /* Simple mode - name field + optional email for Opioid */
          <div className="space-y-4 mb-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Input
                  data-testid="student-name-input"
                  placeholder="Enter student name..."
                  value={newStudentName}
                  onChange={(e) => setNewStudentName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="input-dark h-12 text-lg"
                />
              </div>
              {!isOpioid && (
                <Button
                  onClick={addStudent}
                  className="btn-primary-glow h-12 px-6"
                  data-testid="add-student-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Student
                </Button>
              )}
            </div>
            {/* Optional email field for Opioid course */}
            {isOpioid && (
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    data-testid="student-email-input-opioid"
                    placeholder="Student email (optional - for emailing certificate)"
                    value={newStudentEmail}
                    onChange={(e) => setNewStudentEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="input-dark h-12"
                  />
                </div>
                <Button
                  onClick={addStudent}
                  className="btn-primary-glow h-12 px-6"
                  data-testid="add-student-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Student
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Roster mode - name + email + phone + status fields */
          <div className="space-y-4 mb-4 p-4 bg-zinc-900/30 rounded-lg border border-zinc-800">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Student Name <span className="text-red-500">*</span></Label>
                <Input
                  data-testid="student-name-input"
                  placeholder="Enter student name..."
                  value={newStudentName}
                  onChange={(e) => setNewStudentName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="input-dark h-12"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  placeholder="student@email.com"
                  value={newStudentEmail}
                  onChange={(e) => setNewStudentEmail(e.target.value)}
                  className="input-dark h-12"
                  data-testid="student-email-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Phone <span className="text-red-500">*</span></Label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={newStudentPhone}
                  onChange={(e) => setNewStudentPhone(e.target.value)}
                  className="input-dark h-12"
                  data-testid="student-phone-input"
                />
              </div>
            </div>
            {/* Address Fields */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-1 md:col-span-2">
                <Label className="text-zinc-400 text-sm font-medium">Street Address</Label>
                <Input
                  placeholder="123 Main St"
                  value={newStudentAddress}
                  onChange={(e) => setNewStudentAddress(e.target.value)}
                  className="input-dark h-12"
                  data-testid="student-address-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">City</Label>
                <Input
                  placeholder="City"
                  value={newStudentCity}
                  onChange={(e) => setNewStudentCity(e.target.value)}
                  className="input-dark h-12"
                  data-testid="student-city-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-sm font-medium">State</Label>
                  <Input
                    placeholder="CA"
                    value={newStudentState}
                    onChange={(e) => setNewStudentState(e.target.value.toUpperCase())}
                    maxLength={2}
                    className="input-dark h-12"
                    data-testid="student-state-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-sm font-medium">Zip</Label>
                  <Input
                    placeholder="12345"
                    value={newStudentZip}
                    onChange={(e) => setNewStudentZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    className="input-dark h-12"
                    data-testid="student-zip-input"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-sm font-medium">Status <span className="text-red-500">*</span></Label>
                <Select
                  value={newStudentStatus}
                  onValueChange={setNewStudentStatus}
                >
                  <SelectTrigger className="h-12 input-dark" data-testid="student-status-select">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="Pass" className="hover:bg-zinc-800">Pass</SelectItem>
                    <SelectItem value="Incomplete" className="hover:bg-zinc-800">Incomplete</SelectItem>
                    <SelectItem value="REM/PASS" className="hover:bg-zinc-800">REM/PASS</SelectItem>
                    <SelectItem value="REM/FAIL" className="hover:bg-zinc-800">REM/FAIL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(newStudentStatus === "REM/PASS" || newStudentStatus === "REM/FAIL") && (
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-sm font-medium">Remediation Date</Label>
                  <Input
                    type="date"
                    value={newStudentRemedDate}
                    onChange={(e) => setNewStudentRemedDate(e.target.value)}
                    className="input-dark h-12"
                    data-testid="remediation-date-input"
                  />
                </div>
              )}
              {/* Score field - for BLS courses or Heartsaver with Exam Given */}
              {showScoreField && (
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-sm font-medium">Score <span className="text-zinc-500 text-xs">(optional)</span></Label>
                  <Input
                    placeholder="e.g., 84%"
                    value={newStudentScore}
                    onChange={(e) => setNewStudentScore(e.target.value)}
                    className="input-dark h-12"
                    data-testid="student-score-input"
                  />
                </div>
              )}
              {/* Instructor Selector - only shown when there are assisting instructors */}
              {hasAssistingInstructors && (
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-sm font-medium">Assigned Instructor</Label>
                  <Select
                    value={newStudentInstructor}
                    onValueChange={(value) => setNewStudentInstructor(value)}
                  >
                    <SelectTrigger className="h-12 input-dark">
                      <SelectValue placeholder="Select instructor..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      {allInstructors.map((instructor) => (
                        <SelectItem 
                          key={instructor.id} 
                          value={instructor.id}
                          className="hover:bg-zinc-800 focus:bg-zinc-800"
                        >
                          {instructor.name} ({instructor.initials})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-end">
                <Button
                  onClick={addStudent}
                  className="btn-primary-glow h-12 px-6 w-full"
                  data-testid="add-student-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Student
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Import from Enrollware - Hidden for Instructor Candidate courses */}
        {!isInstructorCandidate && (
          <div className="flex items-center gap-4 pt-4 border-t border-zinc-800">
            <input
              type="file"
              id="excel-upload"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              className="hidden"
              data-testid="excel-upload-input"
            />
            <label htmlFor="excel-upload">
              <Button
                variant="outline"
                className="border-cyan/30 text-cyan hover:bg-cyan/10 cursor-pointer"
                disabled={isImporting}
                asChild
              >
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  {isImporting ? "Importing..." : "Upload Enrollware Student Export File"}
                </span>
              </Button>
            </label>
            <button
              onClick={() => setShowEnrollwareHelp(true)}
              className="text-xs text-cyan hover:text-white underline cursor-pointer"
              data-testid="enrollware-help-btn"
            >
              Getting Enrollware Student Export File
            </button>
          </div>
        )}

        {/* Enrollware Instructions Dialog */}
        <Dialog open={showEnrollwareHelp} onOpenChange={setShowEnrollwareHelp}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Upload className="w-5 h-5 text-cyan" />
                Export from Enrollware
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-zinc-300">
              <p className="text-sm text-zinc-400">Follow these steps to export your student list:</p>
              <ol className="list-decimal list-inside space-y-3 text-sm">
                <li>Go to your class in Enrollware</li>
                <li>Click the button <span className="text-cyan font-medium">Print Cards</span></li>
                <li>Click the tab at the top right <span className="text-cyan font-medium">"Ecards Export"</span></li>
                <li>Make sure all your students are selected by checking the box next to their names on the left side</li>
                <li>Ensure the file type at the bottom is set to <span className="text-cyan font-medium">AHA.XLSX</span></li>
                <li>Click <span className="text-cyan font-medium">Generate Export File</span></li>
                <li>The file will download to your computer, use that file here in the upload Enrollware student file button</li>
              </ol>
            </div>
          </DialogContent>
        </Dialog>

        {/* Max Students Warning Dialog */}
        <Dialog open={showMaxStudentsWarning} onOpenChange={setShowMaxStudentsWarning}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2 text-amber-400">
                <Users className="w-5 h-5" />
                Maximum Students Reached
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-zinc-300">
              <p className="text-sm">
                This system allows a maximum of <span className="text-cyan font-bold">{MAX_STUDENTS} students</span> per class roster.
              </p>
              <p className="text-sm text-zinc-400">
                Please complete a manual roster for any additional students beyond this limit.
              </p>
              <Button
                onClick={() => setShowMaxStudentsWarning(false)}
                className="w-full btn-primary-glow"
                data-testid="close-max-students-dialog"
              >
                OK, Got It
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </Card>

      {/* Student List */}
      <div className="space-y-3 mb-8">
        <AnimatePresence>
          {data.students.map((student, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="student-row"
              data-testid={`student-row-${index}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                    <User className="w-5 h-5 text-cyan" />
                  </div>
                  <div className="flex-1">
                    {/* Editable name and email for simple mode */}
                    {!isRosterMode && editingIndex === index ? (
                      <div className="space-y-2">
                        <Input
                          value={student.name}
                          onChange={(e) => updateStudent(index, 'name', e.target.value)}
                          className="input-dark h-8 text-sm"
                          placeholder="Student name"
                          autoFocus
                        />
                        {isOpioid && (
                          <Input
                            type="email"
                            value={student.email || ""}
                            onChange={(e) => updateStudent(index, 'email', e.target.value)}
                            className="input-dark h-8 text-sm"
                            placeholder="Email (optional)"
                          />
                        )}
                        <Button
                          size="sm"
                          onClick={() => setEditingIndex(null)}
                          className="h-7 px-3 text-xs bg-cyan/20 hover:bg-cyan/30 text-cyan"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Done
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="text-lg">{student.name}</span>
                        {(isRosterMode || isOpioid) && student.email && (
                          <p className="text-xs text-zinc-500">{student.email}</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                {/* Editable roster fields */}
                {isRosterMode && !isInstructorCandidate && (
                  <div className="flex flex-wrap items-center gap-3 mt-3 ml-13 pl-12">
                    <div className="flex items-center gap-2">
                      <Label className="text-zinc-500 text-xs">Status: <span className="text-red-500">*</span></Label>
                      <Select
                        value={student.status || ""}
                        onValueChange={(value) => updateStudent(index, 'status', value)}
                      >
                        <SelectTrigger className={`h-8 w-32 input-dark text-xs ${!student.status ? 'border-red-500/50' : ''}`}>
                          <SelectValue placeholder="Choose one" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800">
                          <SelectItem value="Pass" className="hover:bg-zinc-800">Pass</SelectItem>
                          <SelectItem value="Incomplete" className="hover:bg-zinc-800">Incomplete</SelectItem>
                          <SelectItem value="REM/PASS" className="hover:bg-zinc-800">REM/PASS</SelectItem>
                          <SelectItem value="REM/FAIL" className="hover:bg-zinc-800">REM/FAIL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {(student.status === "REM/PASS" || student.status === "REM/FAIL") && (
                      <div className="flex items-center gap-2">
                        <Label className="text-zinc-500 text-xs">Remed. Date:</Label>
                        <Input
                          type="date"
                          value={student.remediationDate || ""}
                          onChange={(e) => updateStudent(index, 'remediationDate', e.target.value)}
                          className="input-dark h-8 w-36 text-xs"
                        />
                      </div>
                    )}
                    
                    {/* Score field - for BLS or Heartsaver with Exam Given */}
                    {showScoreField && (
                      <div className="flex items-center gap-2">
                        <Label className="text-zinc-500 text-xs">Score:</Label>
                        <Input
                          placeholder="e.g., 84%"
                          value={student.score || ""}
                          onChange={(e) => updateStudent(index, 'score', e.target.value)}
                          className="input-dark h-8 w-24 text-xs"
                        />
                      </div>
                    )}
                    
                    {/* Instructor Selector - only shown when there are assisting instructors */}
                    {hasAssistingInstructors && (
                      <div className="flex items-center gap-2">
                        <Label className="text-zinc-500 text-xs">Instructor:</Label>
                        <Select
                          value={student.assignedInstructor?.id || "lead"}
                          onValueChange={(value) => {
                            const selectedInstructor = allInstructors.find(i => i.id === value);
                            if (selectedInstructor) {
                              updateStudent(index, 'assignedInstructor', {
                                id: selectedInstructor.id,
                                name: selectedInstructor.name,
                                initials: selectedInstructor.initials,
                                number: selectedInstructor.number
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 w-40 input-dark text-xs">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800">
                            {allInstructors.map((instructor) => (
                              <SelectItem 
                                key={instructor.id} 
                                value={instructor.id}
                                className="hover:bg-zinc-800 text-xs"
                              >
                                {instructor.name} ({instructor.initials})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Label className="text-zinc-500 text-xs">Email: <span className="text-red-500">*</span></Label>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={student.email || ""}
                        onChange={(e) => updateStudent(index, 'email', e.target.value)}
                        className={`input-dark h-8 w-44 text-xs ${!student.email ? 'border-red-500/50' : ''}`}
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Label className="text-zinc-500 text-xs">Phone: <span className="text-red-500">*</span></Label>
                      <Input
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={student.phone || ""}
                        onChange={(e) => updateStudent(index, 'phone', e.target.value)}
                        className={`input-dark h-8 w-36 text-xs ${!student.phone ? 'border-red-500/50' : ''}`}
                      />
                    </div>
                  </div>
                )}
                
                {/* IC-specific info display */}
                {isInstructorCandidate && editingIndex !== index && (
                  <div className="mt-3 ml-13 pl-12 space-y-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      {student.email && (
                        <div><span className="text-zinc-500">Email:</span> <span className="text-zinc-300">{student.email}</span></div>
                      )}
                      {student.phone && (
                        <div><span className="text-zinc-500">Phone:</span> <span className="text-zinc-300">{student.phone}</span></div>
                      )}
                      {student.mailingAddress && (
                        <div className="col-span-2"><span className="text-zinc-500">Address:</span> <span className="text-zinc-300">{student.mailingAddress}, {student.city}, {student.state} {student.zipCode}</span></div>
                      )}
                      {student.candidateInstructorId && (
                        <div><span className="text-zinc-500">Instructor ID:</span> <span className="text-zinc-300">{student.candidateInstructorId}</span></div>
                      )}
                      {student.candidateSignature && (
                        <div><span className="text-green-400 text-xs">✓ Signature saved</span></div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* IC student edit form */}
                {isInstructorCandidate && editingIndex === index && (
                  <div className="mt-3 ml-13 pl-12 space-y-3 p-4 bg-zinc-900/50 rounded-lg border border-zinc-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Name</Label>
                        <Input
                          value={student.name}
                          onChange={(e) => updateStudent(index, 'name', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Provider Card Renewal Date</Label>
                        <Input
                          type="date"
                          value={student.providerCardRenewalDate || ""}
                          onChange={(e) => updateStudent(index, 'providerCardRenewalDate', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Mailing Address</Label>
                      <Input
                        value={student.mailingAddress || ""}
                        onChange={(e) => updateStudent(index, 'mailingAddress', e.target.value)}
                        className="input-dark h-9 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">City</Label>
                        <Input
                          value={student.city || ""}
                          onChange={(e) => updateStudent(index, 'city', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">State</Label>
                        <Input
                          value={student.state || ""}
                          onChange={(e) => updateStudent(index, 'state', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Zip Code</Label>
                        <Input
                          value={student.zipCode || ""}
                          onChange={(e) => updateStudent(index, 'zipCode', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Phone</Label>
                        <Input
                          value={student.phone || ""}
                          onChange={(e) => updateStudent(index, 'phone', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Email</Label>
                        <Input
                          value={student.email || ""}
                          onChange={(e) => updateStudent(index, 'email', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-zinc-500">Candidate Instructor ID</Label>
                        <Input
                          value={student.candidateInstructorId || ""}
                          onChange={(e) => updateStudent(index, 'candidateInstructorId', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Renewal Date</Label>
                        <Input
                          type="date"
                          value={student.candidateRenewalDate || ""}
                          onChange={(e) => updateStudent(index, 'candidateRenewalDate', e.target.value)}
                          className="input-dark h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end pt-2">
                      <Button
                        size="sm"
                        onClick={() => setEditingIndex(null)}
                        className="bg-cyan hover:bg-cyan/80"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Done
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                {/* Edit button - show for simple mode (non-roster) and IC students when not already editing */}
                {(!isRosterMode || isInstructorCandidate) && editingIndex !== index && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingIndex(index)}
                    className="text-zinc-400 hover:text-cyan hover:bg-cyan/10"
                    data-testid={`edit-student-${index}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeStudent(index)}
                  className="text-zinc-400 hover:text-red-500 hover:bg-red-500/10"
                  data-testid={`remove-student-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {data.students.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No students added yet</p>
            <p className="text-sm">Add students using the form above</p>
          </div>
        )}
      </div>

      {/* Mark All Pass Button - Only show when roster mode and students exist */}
      {isRosterMode && data.students.length > 0 && (
        <div className="flex justify-center mb-4">
          <Button
            onClick={markAllPass}
            variant="outline"
            className="border-green-600 text-green-500 hover:bg-green-600/10 hover:text-green-400"
            data-testid="mark-all-pass-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            Mark All Pass
          </Button>
        </div>
      )}

      {/* Date of Class - shown after Mark All Pass button when students exist */}
      {data.students.length > 0 && (
        <div className="mb-6 p-4 bg-sky-900/30 rounded-lg border border-sky-600/50">
          <Label className="text-sky-300 text-sm font-medium">
            Date of Class <span className="text-red-500">*</span>
          </Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="date-picker-trigger"
                className="w-full md:w-64 h-12 mt-2 justify-start text-left font-normal bg-sky-800/40 border-sky-600/50 hover:bg-sky-700/50 text-white"
              >
                {data.classDate ? (
                  format(new Date(data.classDate + 'T00:00:00'), "MM/dd/yyyy")
                ) : (
                  <span className="text-sky-300/70">Select date...</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-zinc-950 border-zinc-800" align="start">
              <Calendar
                mode="single"
                selected={data.classDate ? new Date(data.classDate + 'T00:00:00') : undefined}
                onSelect={(date) => {
                  setData({ ...data, classDate: date ? format(date, "yyyy-MM-dd") : "" });
                  setCalendarOpen(false);
                }}
                data-testid="calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex gap-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="flex-1 h-14 border-zinc-700 hover:bg-zinc-900"
          data-testid="back-to-setup-btn"
        >
          <ChevronLeft className="mr-2 w-5 h-5" />
          Back to Setup
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 h-14 btn-primary-glow text-lg font-semibold"
          data-testid="next-to-export-btn"
        >
          Continue to Export
          <ChevronRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </motion.div>
  );
};

// Step 3: Export Options
const ExportOptions = ({ data, setData, onBack, onStartOver }) => {
  const [selectedExport, setSelectedExport] = useState(data.exportType || "");
  const [isExporting, setIsExporting] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isEmailingPaperwork, setIsEmailingPaperwork] = useState(false);
  const [isEmailingTC, setIsEmailingTC] = useState(false);
  const [showEmailPaperworkDialog, setShowEmailPaperworkDialog] = useState(false);
  const [paperworkRecipientEmail, setPaperworkRecipientEmail] = useState("");
  const [exportProgress, setExportProgress] = useState({ stage: '', detail: '' });
  const [previewData, setPreviewData] = useState(null);
  const [isPrintPreview, setIsPrintPreview] = useState(false);
  const signatureRef = useRef(null);
  
  // Check if this is an Opioid course
  const isOpioidCourse = data.courseType === "Opioid Awareness from BLCPR";
  
  // Check if this is an Instructor Candidate course
  const isInstructorCandidate = data.courseType === "Instructor Candidate";
  
  // Check if this is a BLS or Heartsaver course (for email paperwork button)
  const isBLSOrHSCourse = [
    "Basic Life Support (BLS)",
    "HS CPR/AED", "HS FA CPR/AED", "HS First Aid", "HS Total", 
    "HS Pediatric First Aid CPR/AED", "HS K-12",
    "Instructor Candidate"
  ].includes(data.courseType);
  
  // Get students with emails for Opioid course
  const studentsWithEmails = data.students.filter(s => s.email);
  const hasStudentsWithEmails = studentsWithEmails.length > 0;

  // Restore signature from saved data when component mounts
  useEffect(() => {
    if (signatureRef.current && data.instructorSignature) {
      const timer = setTimeout(() => {
        if (signatureRef.current) {
          signatureRef.current.fromDataURL(data.instructorSignature);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  // Clear signature
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setData({ ...data, instructorSignature: null });
    }
  };

  // Save signature when drawing ends
  const saveSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureData = signatureRef.current.toDataURL('image/png');
      setData({ ...data, instructorSignature: signatureData });
    }
  };

  // Update parent data when export type changes
  const handleExportTypeChange = (value) => {
    setSelectedExport(value);
    setData({ ...data, exportType: value });
  };

  // Fetch preview data only once when component mounts
  useEffect(() => {
    const fetchPreview = async () => {
      if (data.students.length === 0) return;
      try {
        const response = await axios.post(`${API}/preview-templates`, {
          class_setup: {
            instructor_initials: data.instructorInitials,
            instructor_number: data.instructorNumber,
            class_date: data.classDate,
            course_type: data.courseType,
            age_option: data.ageOption || null
          },
          students: data.students
        });
        setPreviewData(response.data);
      } catch (error) {
        console.error("Preview error:", error);
      }
    };
    fetchPreview();
    // Only fetch once when component mounts, not on every data change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreview = () => {
    if (data.students.length === 0) {
      toast.error("Add at least one student to preview");
      return;
    }

    // Create a form data object to send as query params
    const params = new URLSearchParams({
      instructor_initials: data.instructorInitials,
      instructor_number: data.instructorNumber,
      class_date: data.classDate,
      course_type: data.courseType,
      age_option: data.ageOption || '',
      student_name: data.students[0].name
    });
    
    const previewUrl = `${API}/preview-pdf-view?${params.toString()}`;
    
    // Try to open in new tab, if blocked, download instead
    const newWindow = window.open(previewUrl, '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Popup was blocked, download instead
      toast.info("Opening preview... If blocked, check your downloads.");
      window.location.href = previewUrl;
    }
  };

  const handlePrintPreview = async () => {
    if (data.students.length === 0) {
      toast.error("Add at least one student to print");
      return;
    }

    setIsPrintPreview(true);
    try {
      // Generate the combined PDF for all students
      const response = await axios.post(
        `${API}/export`,
        {
          class_setup: {
            instructor_initials: data.instructorInitials,
            instructor_number: data.instructorNumber,
            class_date: data.classDate,
            course_type: data.courseType,
            age_option: data.ageOption || null
          },
          students: data.students,
          export_type: "combined"
        },
        { responseType: "blob" }
      );

      // Create a blob URL and open in new window for print preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab for preview
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        toast.success("Print preview opened in new tab. Use browser's print function (Ctrl+P).");
      } else {
        toast.error("Popup blocked. Please allow popups for this site.");
      }
    } catch (error) {
      console.error("Print preview error:", error);
      toast.error("Failed to generate print preview.");
    } finally {
      setIsPrintPreview(false);
    }
  };

  const handlePrint = async () => {
    if (data.students.length === 0) {
      toast.error("Add at least one student to print");
      return;
    }

    setIsPrintPreview(true);
    try {
      // Build the payload similar to handleExport
      const isHSCourse = ["HS CPR/AED", "HS FA CPR/AED", "HS First Aid", "HS Total", "HS Pediatric First Aid CPR/AED", "HS K-12"].includes(data.courseType);
      const isBLSCourse = data.courseType === "Basic Life Support (BLS)";
      const isOpioidCourse = data.courseType === "Opioid Awareness from BLCPR";
      const isBLSInstructorCandidate = data.courseType === "Instructor Candidate" && data.instructorCandidateType === "BLS Instructor Candidate";
      const isHSInstructorCandidate = data.courseType === "Instructor Candidate" && data.instructorCandidateType === "HS Instructor Candidate";

      const payload = {
        class_setup: {
          instructor_initials: data.instructorInitials || "",
          instructor_number: data.instructorNumber || "",
          class_date: data.classDate,
          course_type: data.courseType,
          age_option: data.ageOption || null,
          bls_cert_type: data.blsCertType || null,
          instructor_candidate_type: data.instructorCandidateType || null,
          lead_instructor_name: data.leadInstructorName || "",
          include_roster: data.includeRoster || false,
          blank_printable_mode: data.blankPrintableMode || false
        },
        students: data.students,
        export_type: "combined"
      };

      // Add roster_info if applicable
      if (data.includeRoster && (isBLSCourse || isBLSInstructorCandidate || isHSCourse || isHSInstructorCandidate)) {
        payload.roster_info = {
          lead_instructor_name: data.leadInstructorName || "",
          course_location: data.courseLocation || "",
          course_start_time: data.courseStartTime || "",
          course_end_time: data.courseEndTime || "",
          total_hours: data.totalHours || "",
          manikin_ratio: data.manikinRatio || "",
          assisting_instructors: (data.assistingInstructors || []).filter(i => i && i.name),
          exam_given: data.examGiven || false,
          instructor_signature: data.instructorSignature || null
        };
      }

      // Add roster_info for Opioid (needs signature)
      if (isOpioidCourse) {
        payload.roster_info = {
          lead_instructor_name: data.leadInstructorName || "",
          instructor_signature: data.instructorSignature || null
        };
      }

      // Add instructor_candidate_info for courses with roster (TC info)
      const isInstructorCandidate = data.courseType === "Instructor Candidate";
      if (isInstructorCandidate || data.includeRoster) {
        const icInfo = data.instructorCandidateInfo || {};
        payload.instructor_candidate_info = {
          tc_name: icInfo.tcName || "",
          tc_id: icInfo.tcId || "",
          training_site: icInfo.trainingSite || "",
          tc_address: icInfo.tcAddress || "",
          tc_city_state_zip: icInfo.tcCityStateZip || "",
          signature_date: icInfo.signatureDate || ""
        };
      }

      // Generate the combined PDF for all students
      const response = await axios.post(
        `${API}/export`,
        payload,
        { responseType: "blob" }
      );

      // Create a blob URL
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          toast.success("Print dialog opened!");
        }, 500);
      };
      
      document.body.appendChild(iframe);
      
      // Clean up after printing (give time for print dialog)
      setTimeout(() => {
        document.body.removeChild(iframe);
        window.URL.revokeObjectURL(url);
      }, 60000);
      
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print. Please try exporting and printing manually.");
    } finally {
      setIsPrintPreview(false);
    }
  };

  const handleExport = async () => {
    if (!selectedExport || isExporting) {
      if (!selectedExport) toast.error("Please select an export option");
      return;
    }

    setIsExporting(true);
    setExportProgress({ stage: 'Preparing', detail: 'Building export request...' });
    
    try {
      // Build the request payload
      const payload = {
        class_setup: {
          instructor_initials: data.instructorInitials,
          instructor_number: data.instructorNumber,
          class_date: data.classDate,
          course_type: data.courseType,
          age_option: data.ageOption || null,
          bls_cert_type: data.blsCertType || null,
          instructor_candidate_type: data.instructorCandidateType || null,
          include_roster: data.includeRoster || false,
          blank_printable_mode: data.blankPrintableMode || false,
          lead_instructor_name: data.leadInstructorName || ""
        },
        students: data.students,
        export_type: selectedExport
      };

      // Add roster_info if includeRoster is enabled and course supports roster
      const isBLSInstructorCandidate = data.courseType === "Instructor Candidate" && 
                                        data.instructorCandidateType === "BLS Instructor Candidate";
      const isHSInstructorCandidate = data.courseType === "Instructor Candidate" && 
                                       data.instructorCandidateType === "HS Instructor Candidate";
      const isHSCourse = ["HS CPR/AED", "HS FA CPR/AED", "HS First Aid", "HS Total", "HS Pediatric First Aid CPR/AED", "HS K-12"].includes(data.courseType);
      const isBLSCourse = data.courseType === "Basic Life Support (BLS)";
      const isOpioidCourse = data.courseType === "Opioid Awareness from BLCPR";
      
      if (data.includeRoster && (isBLSCourse || isBLSInstructorCandidate || isHSCourse || isHSInstructorCandidate)) {
        payload.roster_info = {
          lead_instructor_name: data.leadInstructorName || "",
          course_location: data.courseLocation || "",
          course_start_time: data.courseStartTime || "",
          course_end_time: data.courseEndTime || "",
          total_hours: data.totalHours || "",
          manikin_ratio: data.manikinRatio || "",
          assisting_instructors: (data.assistingInstructors || []).filter(i => i && i.name),
          exam_given: data.examGiven || false,
          instructor_signature: data.instructorSignature || null
        };
      }
      
      // Add roster_info for Opioid (just needs signature and instructor name)
      if (isOpioidCourse) {
        payload.roster_info = {
          lead_instructor_name: data.leadInstructorName || "",
          instructor_signature: data.instructorSignature || null
        };
      }
      
      // Add instructor_candidate_info for courses with roster (TC info)
      const isInstructorCandidate = data.courseType === "Instructor Candidate";
      if (isInstructorCandidate || data.includeRoster) {
        const icInfo = data.instructorCandidateInfo || {};
        payload.instructor_candidate_info = {
          tc_name: icInfo.tcName || "",
          tc_id: icInfo.tcId || "",
          training_site: icInfo.trainingSite || "",
          tc_address: icInfo.tcAddress || "",
          tc_city_state_zip: icInfo.tcCityStateZip || "",
          signature_date: icInfo.signatureDate || ""
        };
      }
      
      // Also add full roster_info for IC courses (includes all roster details)
      if (isInstructorCandidate) {
        payload.roster_info = {
          lead_instructor_name: data.leadInstructorName || "",
          course_location: data.courseLocation || "",
          course_start_time: data.courseStartTime || "",
          course_end_time: data.courseEndTime || "",
          total_hours: data.totalHours || "",
          manikin_ratio: data.manikinRatio || "",
          assisting_instructors: (data.assistingInstructors || []).filter(i => i && i.name),
          exam_given: data.examGiven || false,
          instructor_signature: data.instructorSignature || null
        };
      }

      setExportProgress({ 
        stage: 'Generating', 
        detail: `Processing ${data.students.length} student${data.students.length !== 1 ? 's' : ''}...` 
      });

      // Use fetch instead of axios for binary downloads (more reliable)
      const response = await fetch(`${API}/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      setExportProgress({ stage: 'Finalizing', detail: 'Preparing download...' });

      // Get the blob directly from fetch response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const contentDisposition = response.headers.get("content-disposition");
      let filename = `class_${data.classDate}`;
      if (contentDisposition) {
        // Handle both quoted and unquoted filenames
        const match = contentDisposition.match(/filename[*]?=(?:UTF-8'')?["']?([^"';\n]+)["']?/i);
        if (match) {
          filename = decodeURIComponent(match[1].trim());
        }
      } else {
        filename += selectedExport === "combined" ? ".pdf" : ".zip";
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export complete! Check your downloads.");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
      setExportProgress({ stage: '', detail: '' });
    }
  };

  // Handle emailing certificates (Opioid course only)
  const handleEmailCertificates = async () => {
    if (!isOpioidCourse || !hasStudentsWithEmails) return;
    
    // Require signature before emailing
    if (!data.instructorSignature) {
      toast.error("Please add your signature before emailing certificates");
      return;
    }
    
    setIsEmailing(true);
    
    try {
      const payload = {
        class_setup: {
          instructor_initials: data.instructorInitials || "",
          instructor_number: data.instructorNumber || "",
          class_date: data.classDate,
          course_type: data.courseType,
          lead_instructor_name: data.leadInstructorName || ""
        },
        students: studentsWithEmails.map(s => ({ name: s.name, email: s.email })),
        roster_info: {
          lead_instructor_name: data.leadInstructorName || "",
          instructor_signature: data.instructorSignature || null
        }
      };
      
      const response = await axios.post(`${API}/email-certificates`, payload);
      
      const { results } = response.data;
      
      if (results.success.length > 0) {
        toast.success(`Successfully emailed ${results.success.length} certificate(s)!`);
      }
      
      if (results.failed.length > 0) {
        toast.error(`Failed to email ${results.failed.length} certificate(s). Check console for details.`);
        console.error("Failed emails:", results.failed);
      }
      
    } catch (error) {
      console.error("Email error:", error);
      toast.error(error.response?.data?.detail || "Failed to email certificates. Please try again.");
    } finally {
      setIsEmailing(false);
    }
  };

  // Handle emailing paperwork to instructor (BLS/Heartsaver courses)
  const handleEmailPaperwork = async () => {
    if (!isBLSOrHSCourse) return;
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!paperworkRecipientEmail || !emailRegex.test(paperworkRecipientEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setIsEmailingPaperwork(true);
    
    try {
      // Build payload similar to export
      const isBLSInstructorCandidate = data.courseType === "Instructor Candidate" && 
                                        data.instructorCandidateType === "BLS Instructor Candidate";
      const isHSInstructorCandidate = data.courseType === "Instructor Candidate" && 
                                       data.instructorCandidateType === "HS Instructor Candidate";
      const isHSCourse = ["HS CPR/AED", "HS FA CPR/AED", "HS First Aid", "HS Total", "HS Pediatric First Aid CPR/AED", "HS K-12"].includes(data.courseType);
      const isBLSCourse = data.courseType === "Basic Life Support (BLS)";

      const payload = {
        class_setup: {
          instructor_initials: data.instructorInitials || "",
          instructor_number: data.instructorNumber || "",
          class_date: data.classDate,
          course_type: data.courseType,
          age_option: data.ageOption || null,
          bls_cert_type: data.blsCertType || null,
          instructor_candidate_type: data.instructorCandidateType || null,
          lead_instructor_name: data.leadInstructorName || "",
          include_roster: data.includeRoster || false
        },
        students: data.students,
        recipient_email: paperworkRecipientEmail
      };

      // Add roster_info if applicable
      if (data.includeRoster && (isBLSCourse || isBLSInstructorCandidate || isHSCourse || isHSInstructorCandidate)) {
        payload.roster_info = {
          lead_instructor_name: data.leadInstructorName || "",
          course_location: data.courseLocation || "",
          course_start_time: data.courseStartTime || "",
          course_end_time: data.courseEndTime || "",
          total_hours: data.totalHours || "",
          manikin_ratio: data.manikinRatio || "",
          assisting_instructors: (data.assistingInstructors || []).filter(i => i && i.name),
          exam_given: data.examGiven || false,
          instructor_signature: data.instructorSignature || null
        };
      }

      // Add instructor_candidate_info for courses with roster (TC info)
      const isInstructorCandidate = data.courseType === "Instructor Candidate";
      if (isInstructorCandidate || data.includeRoster) {
        const icInfo = data.instructorCandidateInfo || {};
        payload.instructor_candidate_info = {
          tc_name: icInfo.tcName || "",
          tc_id: icInfo.tcId || "",
          training_site: icInfo.trainingSite || "",
          tc_address: icInfo.tcAddress || "",
          tc_city_state_zip: icInfo.tcCityStateZip || "",
          signature_date: icInfo.signatureDate || ""
        };
      }
      
      const response = await axios.post(`${API}/email-paperwork`, payload);
      
      toast.success(response.data.message || "Paperwork sent successfully!");
      setShowEmailPaperworkDialog(false);
      setPaperworkRecipientEmail("");
      
    } catch (error) {
      console.error("Email paperwork error:", error);
      toast.error(error.response?.data?.detail || "Failed to email paperwork. Please try again.");
    } finally {
      setIsEmailingPaperwork(false);
    }
  };

  // Handle emailing Instructor Candidate form to TC
  const handleEmailTC = async () => {
    if (!isInstructorCandidate) return;
    
    setIsEmailingTC(true);
    
    try {
      const candidateInfo = data.instructorCandidateInfo || {};
      
      const payload = {
        class_setup: {
          instructor_initials: data.instructorInitials || "",
          instructor_number: data.instructorNumber || "",
          class_date: data.classDate,
          course_type: data.courseType,
          instructor_candidate_type: data.instructorCandidateType || "",
          lead_instructor_name: data.leadInstructorName || ""
        },
        students: data.students,
        instructor_candidate_info: {
          tc_name: candidateInfo.tcName || "",
          tc_id: candidateInfo.tcId || "",
          training_site: candidateInfo.trainingSite || "",
          tc_address: candidateInfo.tcAddress || "",
          tc_city_state_zip: candidateInfo.tcCityStateZip || "",
          signature_date: candidateInfo.signatureDate || ""
        },
        roster_info: {
          lead_instructor_name: data.leadInstructorName || "",
          course_location: data.courseLocation || "",
          course_start_time: data.courseStartTime || "",
          course_end_time: data.courseEndTime || "",
          total_hours: data.totalHours || "",
          manikin_ratio: data.manikinRatio || "",
          assisting_instructors: (data.assistingInstructors || []).filter(i => i && i.name),
          exam_given: data.examGiven || false,
          instructor_signature: data.instructorSignature || null
        }
      };
      
      const response = await axios.post(`${API}/email-tc`, payload);
      
      toast.success(response.data.message || "Applications sent to Training Center!");
      
    } catch (error) {
      console.error("Email TC error:", error);
      toast.error(error.response?.data?.detail || "Failed to email to TC. Please try again.");
    } finally {
      setIsEmailingTC(false);
    }
  };

  const exportOptions = [
    {
      id: "combined",
      title: "Combined PDF",
      description: "All forms merged into a single PDF file",
      icon: FileStack
    },
    {
      id: "per_student",
      title: "Per Student",
      description: "Each student gets their own PDF file (ZIP)",
      icon: User
    },
    {
      id: "individual",
      title: "Individual Files",
      description: "All forms as separate PDF files (ZIP)",
      icon: Files
    }
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="max-w-4xl mx-auto"
      data-testid="export-page"
    >
      {/* Export Progress Overlay */}
      <AnimatePresence>
        {isExporting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
            data-testid="export-progress-overlay"
          >
            <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8 max-w-md w-full mx-4 text-center">
              <div className="mb-6">
                <Loader2 className="w-16 h-16 text-cyan animate-spin mx-auto" />
              </div>
              <h3 className="text-2xl font-bold mb-2 text-white">
                {exportProgress.stage || 'Exporting'}...
              </h3>
              <p className="text-zinc-400 mb-4">
                {exportProgress.detail || 'Please wait while we generate your documents'}
              </p>
              <div className="w-full bg-zinc-800 rounded-full h-2 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan to-red h-full rounded-full animate-pulse" style={{ width: '100%' }} />
              </div>
              <p className="text-xs text-zinc-500 mt-4">
                This may take a moment for larger classes
              </p>
              <div className="mt-6 pt-4 border-t border-zinc-700">
                <p className="text-xs text-zinc-400 leading-relaxed">
                  It is the responsibility of the user to verify paperwork for accuracy to ensure compliance. Biggest Little CPR is not responsible for missing or inaccurate paperwork. If you experience missing or inaccurate paperwork, please contact us at{' '}
                  <a href="mailto:support@biggestlittlecpr.com" className="text-cyan hover:underline">support@biggestlittlecpr.com</a>
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Export Documents
        </h1>
        <p className="text-zinc-400">Choose how you want to export your forms</p>
      </div>

      {/* Summary Card */}
      <Card className="card-dark p-6 mb-8">
        <div className="flex items-center gap-6">
          <div>
            <p className="text-zinc-400 text-sm">Course</p>
            <p className="text-lg font-semibold text-cyan">{data.courseType}</p>
          </div>
          {data.ageOption && (
            <div>
              <p className="text-zinc-400 text-sm">Age Group</p>
              <p className="text-lg font-semibold">{data.ageOption}</p>
            </div>
          )}
          <div>
            <p className="text-zinc-400 text-sm">Students</p>
            <p className="text-lg font-semibold">{data.students.length}</p>
          </div>
          {previewData && (
            <>
              <div>
                <p className="text-zinc-400 text-sm">Total Pages</p>
                <p className="text-lg font-semibold text-red">{previewData.total_pages}</p>
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Total Files</p>
                <p className="text-lg font-semibold text-red">
                  {selectedExport === "combined" 
                    ? 1 
                    : selectedExport === "per_student" 
                      ? data.students.length 
                      : selectedExport === "individual"
                        ? data.students.length * previewData.templates.length
                        : "-"}
                </p>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Export Options Grid */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {exportOptions.map((option) => (
          <div
            key={option.id}
            onClick={() => handleExportTypeChange(option.id)}
            className={`export-card ${selectedExport === option.id ? 'selected' : ''}`}
            data-testid={`export-option-${option.id}`}
          >
            <option.icon className={`w-10 h-10 mb-4 ${selectedExport === option.id ? 'text-red' : 'text-cyan'}`} />
            <h3 className="text-xl font-semibold mb-2">{option.title}</h3>
            <p className="text-zinc-400 text-sm">{option.description}</p>
          </div>
        ))}
      </div>

      {/* Templates Preview */}
      {previewData && previewData.templates.length > 0 && (
        <Card className="card-dark p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan" />
            Forms to be Generated
          </h3>
          <div className="flex flex-wrap gap-2">
            {previewData.templates.map((template, idx) => (
              <span key={idx} className="badge-cyan">
                {template.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Lead Instructor's Signature - Show for roster OR Opioid course */}
      {(data.includeRoster || data.courseType === "Opioid Awareness from BLCPR") && (
        <Card className="card-dark p-6 mb-8">
          <div className="space-y-2">
            <Label className="text-zinc-400 text-sm font-medium">
              {data.courseType === "Opioid Awareness from BLCPR" ? "Instructor Signature" : "Lead Instructor's Signature"} <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-zinc-500 mb-2">
              {data.courseType === "Opioid Awareness from BLCPR" 
                ? "Required before exporting certificates" 
                : "Required before exporting documents with class roster"}
            </p>
            <div className="bg-white rounded-lg p-2">
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  className: 'signature-canvas w-full h-32 rounded',
                  style: { width: '100%', height: '128px' }
                }}
                onEnd={saveSignature}
                data-testid="signature-canvas"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="text-xs border-zinc-700 hover:bg-zinc-900"
                data-testid="clear-signature-btn"
              >
                <Eraser className="w-3 h-3 mr-1" />
                Clear Signature
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Navigation Buttons */}
      <div className="flex flex-col gap-4">
        {/* Main action row */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={onBack}
            className="h-14 border-zinc-700 hover:bg-zinc-900 px-6"
            data-testid="back-to-students-btn"
          >
            <ChevronLeft className="mr-2 w-5 h-5" />
            Back
          </Button>
          <Button
            onClick={handleExport}
            disabled={!selectedExport || isExporting || ((data.includeRoster || data.courseType === "Opioid Awareness from BLCPR") && !data.instructorSignature)}
            className="flex-1 h-14 btn-primary-glow text-lg font-semibold disabled:opacity-50"
            data-testid="export-btn"
          >
            {isExporting ? (
              <>Exporting...</>
            ) : (
              <>
                <Download className="mr-2 w-5 h-5" />
                Export Documents
              </>
            )}
          </Button>
        </div>

        {/* Start New Class button */}
        {onStartOver && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              onClick={onStartOver}
              className="text-zinc-400 hover:text-cyan-400 hover:bg-zinc-800/50"
              data-testid="start-new-class-btn"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Start New Class
            </Button>
          </div>
        )}
        
        {/* Email Certificates button - Only for Opioid course */}
        {isOpioidCourse && (
          <div className="pt-2">
            <Button
              onClick={handleEmailCertificates}
              disabled={!hasStudentsWithEmails || isEmailing || !data.instructorSignature}
              className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="email-certificates-btn"
            >
              {isEmailing ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Sending Emails...
                </>
              ) : (
                <>
                  <Mail className="mr-2 w-5 h-5" />
                  Email Certificates ({studentsWithEmails.length} student{studentsWithEmails.length !== 1 ? 's' : ''})
                </>
              )}
            </Button>
            {!hasStudentsWithEmails && (
              <p className="text-xs text-amber-400 text-center mt-2">
                Add student emails on the Students page to enable email delivery
              </p>
            )}
          </div>
        )}
        
        {/* Email Paperwork button - For BLS and Heartsaver courses */}
        {isBLSOrHSCourse && (
          <div className="pt-2">
            <Button
              onClick={() => setShowEmailPaperworkDialog(true)}
              disabled={data.students.length === 0 || (data.includeRoster && !data.instructorSignature)}
              className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="email-paperwork-btn"
            >
              <Mail className="mr-2 w-5 h-5" />
              Email Paperwork To Instructor
            </Button>
            {data.includeRoster && !data.instructorSignature && (
              <p className="text-xs text-amber-400 text-center mt-2">
                Please add your signature above to enable email
              </p>
            )}
          </div>
        )}
        
        {/* Email Paperwork Dialog */}
        <Dialog open={showEmailPaperworkDialog} onOpenChange={setShowEmailPaperworkDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Email Paperwork To Instructor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="recipient-email">Recipient Email Address</Label>
                <Input
                  id="recipient-email"
                  type="email"
                  placeholder="instructor@example.com"
                  value={paperworkRecipientEmail}
                  onChange={(e) => setPaperworkRecipientEmail(e.target.value)}
                  className="input-dark mt-2"
                  data-testid="paperwork-email-input"
                />
              </div>
              <p className="text-sm text-zinc-400">
                This will send the combined PDF with all rosters and skill sheets to the email address above.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEmailPaperworkDialog(false);
                    setPaperworkRecipientEmail("");
                  }}
                  className="flex-1 border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEmailPaperwork}
                  disabled={isEmailingPaperwork || !paperworkRecipientEmail}
                  className="flex-1 bg-blue-600 hover:bg-blue-500"
                  data-testid="send-paperwork-btn"
                >
                  {isEmailingPaperwork ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 w-4 h-4" />
                      Send Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Email to TC button - Only for Instructor Candidate courses */}
        {isInstructorCandidate && (
          <div className="pt-2">
            {(() => {
              const allHaveSignatures = data.students.length > 0 && data.students.every(s => s.candidateSignature);
              const studentsWithoutSignatures = data.students.filter(s => !s.candidateSignature).length;
              
              return (
                <>
                  <Button
                    onClick={handleEmailTC}
                    disabled={isEmailingTC || !allHaveSignatures}
                    className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="email-tc-btn"
                  >
                    {isEmailingTC ? (
                      <>
                        <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                        Sending to TC...
                      </>
                    ) : (
                      <>
                        <Mail className="mr-2 w-5 h-5" />
                        Email Canidate Applications to TSC
                      </>
                    )}
                  </Button>
                  {data.students.length === 0 && (
                    <p className="text-xs text-amber-400 text-center mt-2">
                      Add students to enable email to TSC
                    </p>
                  )}
                  {data.students.length > 0 && studentsWithoutSignatures > 0 && (
                    <p className="text-xs text-amber-400 text-center mt-2">
                      {studentsWithoutSignatures} student(s) missing signature - all signatures required to email to TSC
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        )}
        
        {/* Print button */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={data.students.length === 0}
            className="flex-1 h-12 border-zinc-600 hover:bg-zinc-900"
            data-testid="print-btn"
          >
            <Printer className="mr-2 w-4 h-4" />
            Print All Documents
          </Button>
        </div>
        
        <p className="text-xs text-zinc-500 text-center">
          Downloads to default location. File Export may take time dependent on export size.
        </p>
      </div>
    </motion.div>
  );
};

// Main App
function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    instructorInitials: "",
    instructorNumber: "",
    leadInstructorName: "",
    classDate: "",
    courseType: "",
    ageOption: "",
    students: [],
    includeRoster: true,  // Default to on
    instructorCandidateInfo: {
      tcName: "",
      tcId: "",
      trainingSite: "",
      tcAddress: "",
      tcCityStateZip: ""
    }  // For Instructor Candidate forms and TC Info - must select from saved TCs
  });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Load saved instructor preferences - called on mount and when auth state may have changed
  const loadPreferences = async () => {
    const token = localStorage.getItem('cpr_auth_token');
    if (!token) {
      setPreferencesLoaded(true);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.preferences) {
        const prefs = response.data.preferences;
        setFormData(prev => ({
          ...prev,
          instructorInitials: prefs.instructor_initials || prev.instructorInitials,
          instructorNumber: prefs.instructor_number || prev.instructorNumber,
          leadInstructorName: prefs.lead_instructor_name || prev.leadInstructorName
        }));
        console.log("Loaded instructor preferences:", prefs);
      }
    } catch (err) {
      console.log("Could not load preferences:", err.message);
    } finally {
      setPreferencesLoaded(true);
    }
  };

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Also listen for login event (triggered by LicenseGate after successful login)
  useEffect(() => {
    const handleLoginEvent = () => {
      console.log("Login event received, loading preferences...");
      loadPreferences();
    };
    
    // Check immediately after a short delay (allows LicenseGate to set token)
    const timeoutId = setTimeout(() => {
      if (!preferencesLoaded) {
        loadPreferences();
      }
    }, 500);
    
    // Listen for login event from LicenseGate
    window.addEventListener('cpr-auth-login', handleLoginEvent);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('cpr-auth-login', handleLoginEvent);
    };
  }, [preferencesLoaded]);

  // Auto-save instructor preferences when they change (debounced)
  useEffect(() => {
    // Don't save until initial preferences are loaded
    if (!preferencesLoaded) return;
    
    const token = localStorage.getItem('cpr_auth_token');
    if (!token) return;
    
    // Debounce the save to avoid too many API calls
    const timeoutId = setTimeout(async () => {
      try {
        await axios.post(`${API}/user/preferences`, {
          instructor_initials: formData.instructorInitials,
          instructor_number: formData.instructorNumber,
          lead_instructor_name: formData.leadInstructorName
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.log("Could not save preferences:", err.message);
      }
    }, 1000); // Save 1 second after user stops typing
    
    return () => clearTimeout(timeoutId);
  }, [formData.instructorInitials, formData.instructorNumber, formData.leadInstructorName, preferencesLoaded]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Check if navigation to a step is allowed
  const canNavigate = (targetStep) => {
    const hasRoster = formData.includeRoster;
    const isBLS = formData.courseType === "Basic Life Support (BLS)";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    if (targetStep === 1) return true;
    
    if (targetStep === 2) {
      // Can go to step 2 if class setup is complete
      const courseAgeOptions = COURSES_WITH_AGE_OPTIONS[formData.courseType];
      const needsAgeOption = !!courseAgeOptions;
      const basicSetupDone = formData.instructorInitials && formData.instructorNumber && 
             formData.classDate && formData.courseType &&
             (!needsAgeOption || formData.ageOption) &&
             (!isBLS || formData.blsCertType) &&
             (!isInstructorCandidate || formData.instructorCandidateType);
      return basicSetupDone;
    }
    
    if (targetStep === 3) {
      if (hasRoster) {
        // Step 3 is students when roster enabled (after roster step 2)
        return canNavigate(2);
      } else {
        // Step 3 is export when no roster - need students
        return canNavigate(2) && formData.students.length > 0;
      }
    }
    
    if (targetStep === 4) {
      // Step 4 is export when roster enabled - need students
      return hasRoster && canNavigate(3) && formData.students.length > 0;
    }
    
    return false;
  };

  const handleStepClick = (step) => {
    setCurrentStep(step);
  };

  // Start Over - reset all fields except instructor preferences
  const handleStartOver = () => {
    setFormData(prev => ({
      instructorInitials: prev.instructorInitials,
      instructorNumber: prev.instructorNumber,
      leadInstructorName: prev.leadInstructorName,
      classDate: "",
      courseType: "",
      ageOption: "",
      students: [],
      includeRoster: true,
      instructorCandidateInfo: {
        tcName: "",
        tcId: "",
        trainingSite: "",
        tcAddress: "",
        tcCityStateZip: ""
      }
    }));
    setCurrentStep(1);
  };
  
  // Get step labels based on roster mode
  const getStepLabels = () => {
    const isOpioid = formData.courseType === "Opioid Awareness from BLCPR";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    if (isOpioid) {
      return ["Class Setup", "Students", "Export"];
    }
    // TC Info shows for all courses with roster (including IC)
    if (isInstructorCandidate || formData.includeRoster) {
      return ["Class Setup", "Class Roster", "TC Info", "Students", "Export"];
    }
    return ["Class Setup", "Students", "Export"];
  };
  
  // Get total steps based on roster mode
  const getTotalSteps = () => {
    const isOpioid = formData.courseType === "Opioid Awareness from BLCPR";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    if (isOpioid) {
      return 3;
    }
    // TC Info shows for all courses with roster (including IC)
    if (isInstructorCandidate || formData.includeRoster) {
      return 5; // Setup -> Roster -> TC Info -> Students -> Export
    }
    return 3;
  };
  
  // Handle navigation with roster awareness
  const handleNextFromSetup = () => {
    const isOpioid = formData.courseType === "Opioid Awareness from BLCPR";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    // Validate Lead Instructor Name if roster is enabled (except for Opioid which doesn't use roster step)
    if (formData.includeRoster && !isOpioid && !formData.leadInstructorName?.trim()) {
      toast.error("Please enter Lead Instructor Name (required for roster)");
      return;
    }
    
    // For Opioid, skip roster step - go directly to students
    if (isOpioid) {
      setCurrentStep(2); // Go to students (step 2 for Opioid)
    } else if (isInstructorCandidate) {
      setCurrentStep(2); // Go to Class Roster (step 2 for IC)
    } else if (formData.includeRoster) {
      setCurrentStep(2); // Go to roster
    } else {
      setCurrentStep(2); // Go directly to students
    }
  };
  
  const handleNextFromRoster = () => {
    // Validate mandatory roster fields (Lead Instructor is now on Class Setup page)
    if (!formData.courseStartTime) {
      toast.error("Please enter Course Start Time");
      return;
    }
    if (!formData.courseEndTime) {
      toast.error("Please enter Course End Time");
      return;
    }
    if (!formData.manikinRatio) {
      toast.error("Please select Student to Manikin Ratio");
      return;
    }
    
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    if (isInstructorCandidate || formData.includeRoster) {
      setCurrentStep(3); // Go to TC Info for IC and roster courses
    } else {
      setCurrentStep(2); // Go to students (no roster)
    }
  };
  
  const handleNextFromStudents = () => {
    const isOpioid = formData.courseType === "Opioid Awareness from BLCPR";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    if (isOpioid) {
      setCurrentStep(3); // For Opioid: Setup(1) -> Students(2) -> Export(3)
    } else if (isInstructorCandidate || formData.includeRoster) {
      setCurrentStep(5); // For roster/IC: Setup(1) -> Roster(2) -> TC Info(3) -> Students(4) -> Export(5)
    } else {
      setCurrentStep(3); // Go to export (no roster)
    }
  };
  
  const handleBackFromStudents = () => {
    const isOpioid = formData.courseType === "Opioid Awareness from BLCPR";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    if (isOpioid) {
      setCurrentStep(1); // Back to setup
    } else if (isInstructorCandidate || formData.includeRoster) {
      setCurrentStep(3); // Back to TC Info
    } else {
      setCurrentStep(1); // Back to setup (no roster)
    }
  };
  
  const handleBackFromExport = () => {
    const isOpioid = formData.courseType === "Opioid Awareness from BLCPR";
    const isInstructorCandidate = formData.courseType === "Instructor Candidate";
    
    if (isOpioid) {
      setCurrentStep(2); // Back to students
    } else if (isInstructorCandidate || formData.includeRoster) {
      setCurrentStep(4); // Back to students (step 4 for roster/IC)
    } else {
      setCurrentStep(2); // Back to students (no roster)
    }
  };

  return (
    <LicenseGate>
      <div className="app-container" data-testid="app-container">
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#09090B',
              border: '1px solid #27272A',
              color: '#fff'
            }
          }}
        />
      <Navigation 
        currentStep={currentStep} 
        onStepClick={handleStepClick}
        canNavigate={canNavigate}
        stepLabels={getStepLabels()}
        totalSteps={getTotalSteps()}
      />
      
      <main className="px-6 py-12">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <ClassSetup 
              key="setup"
              data={formData} 
              setData={setFormData} 
              onNext={handleNextFromSetup}
            />
          )}
          {/* Class Roster - shown for roster-enabled courses and IC courses */}
          {currentStep === 2 && (formData.includeRoster || formData.courseType === "Instructor Candidate") && formData.courseType !== "Opioid Awareness from BLCPR" && (
            <ClassRoster
              key="roster"
              data={formData}
              setData={setFormData}
              onNext={handleNextFromRoster}
              onBack={() => setCurrentStep(1)}
            />
          )}
          {/* TC Info - shown at step 3 for courses with roster (including IC) */}
          {currentStep === 3 && (formData.courseType === "Instructor Candidate" || formData.includeRoster) && formData.courseType !== "Opioid Awareness from BLCPR" && (
            <TCInfo
              key="ic-details"
              data={formData}
              setData={setFormData}
              onNext={() => setCurrentStep(4)}
              onBack={() => setCurrentStep(2)}
            />
          )}
          {/* Student Entry - various conditions based on course type */}
          {(
            (currentStep === 2 && !formData.includeRoster && formData.courseType !== "Instructor Candidate") || 
            (currentStep === 2 && formData.courseType === "Opioid Awareness from BLCPR") ||
            (currentStep === 4 && (formData.courseType === "Instructor Candidate" || formData.includeRoster) && formData.courseType !== "Opioid Awareness from BLCPR")
          ) && (
            <StudentEntry 
              key="students"
              data={formData} 
              setData={setFormData} 
              onNext={handleNextFromStudents}
              onBack={handleBackFromStudents}
            />
          )}
          {/* Export - final step */}
          {(
            (currentStep === 3 && !formData.includeRoster && formData.courseType !== "Opioid Awareness from BLCPR" && formData.courseType !== "Instructor Candidate") || 
            (currentStep === 3 && formData.courseType === "Opioid Awareness from BLCPR") ||
            (currentStep === 5 && (formData.courseType === "Instructor Candidate" || formData.includeRoster) && formData.courseType !== "Opioid Awareness from BLCPR")
          ) && (
            <ExportOptions 
              key="export"
              data={formData}
              setData={setFormData}
              onBack={handleBackFromExport}
              onStartOver={handleStartOver}
            />
          )}
        </AnimatePresence>

        {/* Start Over Button - shown when not on step 1 */}
        {currentStep > 1 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleStartOver}
              className="text-zinc-500 hover:text-red-400 text-sm flex items-center gap-2 transition-colors"
              data-testid="start-over-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Start Over
            </button>
          </div>
        )}
      </main>

      {/* Footer with Logo */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="flex flex-col items-center gap-4">
          <img 
            src={LOGO_URL} 
            alt="Biggest Little CPR" 
            className="h-16 opacity-60"
            data-testid="footer-logo"
          />
          <div className="text-center text-xs text-zinc-500">
            <p>© {new Date().getFullYear()} Biggest Little CPR. All rights reserved.</p>
            <p className="mt-1">
              If you have issues please email us at{' '}
              <a 
                href="mailto:support@biggestlittlecpr.com" 
                className="text-cyan hover:text-white underline"
              >
                support@biggestlittlecpr.com
              </a>
            </p>
            <p className="mt-2 text-zinc-600">v3.0</p>
          </div>
        </div>
      </footer>
    </div>
    </LicenseGate>
  );
}

export default App;
